import React, { useState, useEffect, useRef } from 'react';
import { SocialLinks } from './ui/SocialLinks';
import { BusinessProfileBadges } from './business/BusinessProfileBadges';
import { BusinessSettingsLinks } from './business/BusinessSettingsLinks';
import { FriendBadge } from './ui/FriendBadge';
import { ActivityStatus } from './ui/ActivityStatus';
import { SimilarBadge } from './ui/SimilarBadge';
import { isSimilarToMe } from './utils/similarity';
import { FRIEND_REQUEST_COST, SWIPE_COSTS } from '../constants/economy';
import { EconomyService, AdminService } from '../services/api';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, Reorder } from 'motion/react';
import { Settings, Shield, MapPin, Briefcase, GraduationCap, Plus, Loader2, Users, MessageSquare, Eye, EyeOff, MoreVertical, Pin, Lock, Megaphone, Edit, Trash2, Heart, LogOut, ArrowLeft, Hand, UserPlus, UserMinus, UserCheck, Ban, Play, HandMetal, X, Search, ChevronLeft, ChevronRight, ScrollText } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { useContentFilter } from '../hooks/useContentFilter';
import { ScrollArea } from './ui/scroll-area';
import { ShareProfileModal } from './ShareProfileModal';
import { YapTabGuidelinesModal } from './YapTabGuidelinesModal';
import { ProfilePictureModal } from './ProfilePictureModal';
import { ImageCropperModal } from './ImageCropperModal';
import { EmbeddedYap } from './EmbeddedYap';
import { FormattedText } from './FormattedText';
import { SkeletonProfileHeader, SkeletonYap } from './ui/SkeletonPrimitives';
import { useScreenReady } from '../contexts/ScreenTransitionContext';
import { useUserData } from './UserDataContext';
import { useMetricDelta } from './MetricDeltaContext';
import { useNotificationBadges } from './NotificationContext';
import { useYapInteractions } from './YapInteractionsContext';
import { toast } from 'sonner';
import { ImageWithPreview } from './ImageWithPreview';
import { Switch } from './ui/switch';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { ShoutYapModal } from './ShoutYapModal';
import { ShoutedYapOptionsModal } from './ShoutedYapOptionsModal';
import { ShareShoutModal } from './ShareShoutModal';
import { ShareYapModal as ShareYapInfoModal } from './ShareYapModal'; // Rename to avoid conflict if any
// ShareYapsModal was invalid and was a duplicate of ShareYapInfoModal or the like
import { getGroupGradient } from './utils/groupUtils';
import { getRankRingParams, sanitizeLocation } from './utils/rankUtils';
import RankBadge from './RankBadge';
import { VerifiedBadge } from './VerifiedBadge';
import { FlagImage, countryToFlagCode } from './utils/countryFlags';
import { getFlagImage } from '../assets/flags';
import { useSettings } from './SettingsContext';
import { formatTimeAgo, calculateAge } from './utils/timeFormat';
import { getTribeStyle } from './utils/tribeColors';
import { ParallaxBackground } from './utils/parallax';
import { YapService, UserService, TribeService, NotificationService } from '../services/api';
import { getYapAuthorInfo, getYapInteractionId, getOriginalYapId, isQuoteShout as isQuoteShoutUtil, getYapPostType, isMaybeVideo } from './utils/yapUtils';
import { Yap, Tribe, ShoutedYap, User } from '../types';
import { SCREENS, EVENTS } from '../constants/navigation';
import { PressableCard } from './ui/PressableCard';
import { LikeButton } from './ui/LikeButton';
import { DEFAULT_BANNER_URL } from '../constants/assets';
import { LegionAvatarWrapper } from './LegionAvatarWrapper';
import { uploadProfileBanner } from '../services/api';
import { SafeStorage } from '../utils/safeStorage';
import { VoiceReplyBubble } from './VoiceReplyBubble';
import { CinematicProfileAvatar } from './CinematicProfileAvatar';
import { IdentityTag } from './IdentityTag';
import { formatCount } from '../utils/numberFormat';
import { categoryStyles, getCategoryByTag } from '../constants/identities';
import { getUserAvatar } from '../utils/userUtils';

interface ProfileScreenProps {
  onSettings?: () => void;
  onSafety?: () => void;
  onBack?: () => void;
  userId?: string | number;
  user?: User;
}

// Helper to parse timestamp for sorting
const getTimestampMs = (timeStr: string | undefined): number => {
  if (!timeStr) return 0;
  if (timeStr === 'Just now' || timeStr === 'just now' || timeStr === 'Now' || timeStr === 'now') return Date.now();

  // Try parsing as ISO date
  const date = new Date(timeStr);
  if (!isNaN(date.getTime())) {
    return date.getTime();
  }

  return 0;
};

export function ProfileScreen({ onSettings, onSafety, onBack, userId: propUserId, user: propUser }: ProfileScreenProps) {
  const [showShareModal, setShowShareModal] = useState(false);

  // The guide shows the share card and then puts it away again.
  useEffect(() => {
    const open = () => setShowShareModal(true);
    const close = () => setShowShareModal(false);
    window.addEventListener('guide:openShareProfile', open);
    window.addEventListener('guide:closeShareProfile', close);
    return () => {
      window.removeEventListener('guide:openShareProfile', open);
      window.removeEventListener('guide:closeShareProfile', close);
    };
  }, []);
  // Guidelines opened deliberately from the Rules button, rather than shown by
  // the once-per-account gate in AppRouter. Accepting here only closes it: the
  // account has already agreed, so there is nothing further to record.
  const [showRules, setShowRules] = useState(false);
  const [showPictureModal, setShowPictureModal] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImage, setCropImage] = useState<string | null>(null);
  const [cropType, setCropType] = useState<'profile' | 'banner'>('profile');
  const [isBannerUploading, setIsBannerUploading] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [viewingPortfolioIndex, setViewingPortfolioIndex] = useState<number | null>(null);
  const [viewAsMode, setViewAsMode] = useState<'self' | 'friend' | 'nonfriend'>('self');
  const [userYaps, setUserYaps] = useState<Yap[]>([]);
  const [visitorShoutedYaps, setVisitorShoutedYaps] = useState<ShoutedYap[]>([]);
  const [ownedTribes, setOwnedTribes] = useState<Tribe[]>([]);
  const [visitorFriends, setVisitorFriends] = useState<User[]>([]);
  const [isYapsExpanded, setIsYapsExpanded] = useState(false); // New state for View All Yaps

  // New State for Reordering
  const [isReordering, setIsReordering] = useState(false);
  const [portfolioItems, setPortfolioItems] = useState<string[]>([]);
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const savedScrollY = useRef<number>(0);

  const saveProfileScrollPosition = () => {
    if (scrollContainerRef.current) {
      savedScrollY.current = scrollContainerRef.current.scrollTop;
    }
  };

  // Visitor State
  const [visitorUser, setVisitorUser] = useState<User | null>(propUser || null);
  const [isFriendState, setIsFriendState] = useState(false);
  const [visitorLoading, setVisitorLoading] = useState(!!propUserId && !propUser);
  const [showShoutModal, setShowShoutModal] = useState(false);
  const [showShoutOptionsModal, setShowShoutOptionsModal] = useState(false);
  const [showShareShout, setShowShareShout] = useState(false);
  const [selectedYapToShout, setSelectedYapToShout] = useState<Yap | null>(null);
  const [selectedShoutToShare, setSelectedShoutToShare] = useState<ShoutedYap | null>(null);
  const [showShareYap, setShowShareYap] = useState(false);
  const [selectedYapToShare, setSelectedYapToShare] = useState<Yap | null>(null);
  const [isAvatarExpanded, setIsAvatarExpanded] = useState(false);
  const [showFriendsModal, setShowFriendsModal] = useState(false);
  const [friendsSearch, setFriendsSearch] = useState('');
  // Friends modal pagination — render a slice at a time so users with many
  // friends don't lag rendering the whole list at once.
  const [friendsPage, setFriendsPage] = useState(1);
  const FRIENDS_PAGE_SIZE = 40;

  const { userProfile, updateProfile, shoutedYaps: selfShoutedYaps, getFriendsCount, friends, addShout, removeShout, updateShoutCaption, isFriend, firebaseUser, sendFriendRequest, hasPendingRequestTo, hasPendingRequestFrom, acceptFriendRequest, cancelFriendRequest, friendRequests, isForumMember, isTribeMember, removeFriend: contextRemoveFriend, blockUser: contextBlockUser } = useUserData();
  const { adjustMetric, getOptimisticCount, resetMetric } = useMetricDelta();
  const { addNotification } = useNotificationBadges();
  const { shoutedYaps: globalShoutedYaps, isYapLiked, isYapShouted, getLikeCount, getShoutCount, toggleLike, toggleShout } = useYapInteractions();
  const { profileVisibility } = useSettings();
  const { isBlockedId, filterVisible } = useContentFilter();
  const [isLoading, setIsLoading] = useState(true);
  const [isShaking, setIsShaking] = useState(false);
  const [isProcessingRequest, setIsProcessingRequest] = useState(false);

  const isSelf = !propUserId || String(propUserId) === String(userProfile.id);
  const displayUser = isSelf ? userProfile : (visitorUser || userProfile); // fallback to userProfile during load to avoid crashes
  // The @watcher "AfroYap Moderation Team" system account: hides stats/Yaps/
  // Friends/Tribes and blocks friending. Gated on a per-account Firestore flag —
  // NOT on Watcher rank — so ordinary Watchers are unaffected.
  const isSystemModProfile = (displayUser as any)?.isSystemModAccount === true;
  const derivedIsFriend = isSelf ? true : (isFriendState || isFriend(String(displayUser.id)));
  // Similar users cost the matching price (30); everyone else the base (10) —
  // same rule as the discover page.
  const isSimilarUser = !isSelf && isSimilarToMe(userProfile, displayUser);
  const connectCost = isSimilarUser ? SWIPE_COSTS.matching : FRIEND_REQUEST_COST;
  // Scroll position save helper
  const saveScrollPosition = () => {
    if (scrollContainerRef.current) {
      const position = Math.round(scrollContainerRef.current.scrollTop);
      if (position > 0) {
        console.log(`[ProfileScreen] Saving scroll pos for ${displayUser.id}:`, position);
        SafeStorage.setItem(`scroll_pos_profile_${displayUser.id}`, position.toString());
      }
    }
  };

  const isPrivateAccount = !isSelf && (displayUser as any).profileVisibility === 'private' && !derivedIsFriend;

  console.log('[ProfileScreen] Debug:', {
    propUserId,
    isSelf,
    displayUserId: displayUser?.id,
    displayUserName: displayUser?.name,
    viewAsMode,
    yapsCount: userYaps.length,
    tribesCount: ownedTribes.length
  });

  useScreenReady(!(isLoading || visitorLoading));

  // Initialize portfolio items when userProfile loads
  useEffect(() => {
    if (userProfile.portfolio) {
      setPortfolioItems(userProfile.portfolio);
    }
  }, [userProfile.portfolio]);

  // Fetch Visitor Data
  useEffect(() => {
    const fetchVisitorData = async () => {
      if (!isSelf && propUserId) {
        setVisitorLoading(true);
        // Safety: Clear previous visitor data to prevent "stale" flashes during loading
        setVisitorUser(null);
        setVisitorShoutedYaps([]);
        setVisitorFriends([]);
        try {
          // Parallelize data fetching for faster profile loads
          const [user, shouts, vFriends] = await Promise.all([
            UserService.fetchUserById(String(propUserId)),
            YapService.fetchUserShouts(String(propUserId)),
            UserService.getFriends(String(propUserId)).catch(err => {
              console.error("Error fetching visitor friends", err);
              return [];
            })
          ]);

          if (user) {
            setVisitorUser(user);
            setIsFriendState(isFriend(String(user.id)));
          }
          setVisitorShoutedYaps(shouts);
          setVisitorFriends(vFriends);
          console.log('[ProfileScreen] Visitor Friends fetched:', vFriends.length);

        } catch (error) {
          console.error("Error fetching visitor profile:", error);
        } finally {
          setVisitorLoading(false);
        }
      }
    };
    fetchVisitorData();
  }, [propUserId, isSelf]);

  // Handle scroll to top when user changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [propUserId]);

  // Removed redundant checkOutgoingRequest as it is handled by hasPendingRequestTo in UserDataContext

  // Sync viewAsMode with isSelf/friend status
  useEffect(() => {
    if (isSelf) {
      // 🔒 FIX: If user manually toggled to preview mode ('friend' or 'nonfriend'), 
      // do not forcefully reset them back to 'self' on unrelated state updates.
      if (viewAsMode === 'self') {
        setViewAsMode('self');
      }
    } else {
      // Only forcibly resync to nonfriend if we actively lost friend status 
      // (avoid resetting UI on unrelated re-renders)
      setViewAsMode(derivedIsFriend ? 'friend' : 'nonfriend');
    }
  }, [isSelf, derivedIsFriend]);

  useEffect(() => {
    let isActive = true;
    const fetchYaps = async () => {
      if (displayUser.id) {
        console.log('[ProfileScreen] Fetching yaps for:', displayUser.id);
        try {
          const yaps = await YapService.fetchUserYaps(String(displayUser.id));
          if (!isActive) return;
          console.log('[ProfileScreen] Fetched yaps:', yaps.length);
          // Filter out anonymous yaps - they should never appear in profile
          const filteredYaps = yaps.filter(yap => !yap.isAnonymous);
          setUserYaps(filteredYaps);
        } catch (error) {
          if (!isActive) return;
          console.error("Error fetching user yaps", error);
        } finally {
          if (isActive) {
            setTimeout(() => setIsLoading(false), 500);
          }
        }
      } else {
        console.log('[ProfileScreen] No displayUser.id to fetch yaps');
        setIsLoading(false);
      }
    };
    fetchYaps();
    // Also clear adjustments on mount
    window.dispatchEvent(new CustomEvent('refreshInteractions'));
    
    return () => {
      isActive = false;
    };
  }, [displayUser.id]);

  // Listen for yap creation and update events to refresh the list
  useEffect(() => {
    const handleRefreshYaps = () => {
      console.log('[ProfileScreen] Yap created/updated event received, refreshing yaps...');
      // Re-fetch yaps
      if (displayUser.id) {
        setIsLoading(true);
        YapService.fetchUserYaps(String(displayUser.id))
          .then((yaps) => {
            console.log('[ProfileScreen] Refreshed yaps:', yaps.length);
            const filteredYaps = yaps.filter(yap => !yap.isAnonymous);
            setUserYaps(filteredYaps);
          })
          .catch((err) => console.error("Error refreshing yaps:", err))
          .finally(() => setIsLoading(false));
      }
    };

    window.addEventListener('yapCreated', handleRefreshYaps);
    window.addEventListener('yapUpdated', handleRefreshYaps);
    return () => {
      window.removeEventListener('yapCreated', handleRefreshYaps);
      window.removeEventListener('yapUpdated', handleRefreshYaps);
    };
  }, [displayUser.id]);

  // Restore scroll position when navigating back from yap/shout detail
  useEffect(() => {
    const handleScrollRestore = () => {
      const pos = savedScrollY.current;
      if (pos > 0 && scrollContainerRef.current) {
        requestAnimationFrame(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = pos;
          }
        });
      }
    };
    window.addEventListener('profileScrollRestore', handleScrollRestore);
    return () => {
      window.removeEventListener('profileScrollRestore', handleScrollRestore);
    };
  }, []);

  // 🥊 REAL-TIME SLAP: Listen for incoming slaps while viewing this profile
  useEffect(() => {
    const handleRealTimeSlap = (e: any) => {
      const { fromUserId, slapsBurst } = e.detail;
      
      // If the person we are viewing slapped us, trigger a SHAKE!
      if (String(fromUserId) === String(displayUser.id)) {
        console.log(`[ProfileScreen] LIVE BATTLE: Received ${slapsBurst} slaps from ${displayUser.name}! Shaking...`);
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 500);
      }
    };

    window.addEventListener(EVENTS.REAL_TIME_SLAP, handleRealTimeSlap);
    return () => window.removeEventListener(EVENTS.REAL_TIME_SLAP, handleRealTimeSlap);
  }, [displayUser.id, displayUser.name]);

  useEffect(() => {
    let isActive = true;
    const fetchOwnedTribes = async () => {
      console.log('[ProfileScreen] Fetching tribes for owner:', displayUser.id);
      try {
        const tribes = await TribeService.fetchTribesByOwner(String(displayUser.id));
        if (!isActive) return;
        // University tribes are created by a Legion on a university's behalf, so
        // they accumulate on that one account and read as tribes they personally
        // run. Filtered out of the profile list.
        //
        // Filtered here rather than at the render site so the "Tribes (N)"
        // heading, the View All threshold and the tribesCount stat all agree
        // with what is actually on screen.
        //
        // isUniversityTribe is the legacy field; tribes created before
        // tribeType existed only carry that one, so both are checked.
        const personalTribes = tribes.filter(
          (t: any) => t.tribeType !== 'university' && !t.isUniversityTribe,
        );
        console.log(
          `[ProfileScreen] Fetched owned tribes: ${tribes.length} ` +
          `(${tribes.length - personalTribes.length} university, hidden)`,
        );
        setOwnedTribes(personalTribes);
      } catch (error) {
        if (!isActive) return;
        console.error("Error fetching owned tribes:", error);
      }
    };
    fetchOwnedTribes();
    return () => {
      isActive = false;
    };
  }, [displayUser.ownedTribeIds, displayUser.id]);

  const handleEditProfile = () => {
    window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: 'editProfile' }));
  };

  const handleViewAllFriends = () => {
    setFriendsSearch('');
    setFriendsPage(1);
    setShowFriendsModal(true);
  };



  const handleFriendClick = (friend: any) => {
    window.dispatchEvent(new CustomEvent('navigateToFriendProfile', { detail: friend }));
  };

  // Messaging permission for THIS profile, as seen by the viewer.
  //
  // An absent preference now means friends-only. It used to fall back to
  // 'everyone', which made the opt-in meaningless: the setting was never written
  // to the user document at all, so every profile read as undefined and the
  // Message button showed on all of them regardless of what anyone had chosen.
  const messagePref: 'everyone' | 'friends' | 'none' =
    ((displayUser as any).allowMessages as any) ?? 'friends';
  const canMessage =
    messagePref === 'everyone' || (messagePref === 'friends' && derivedIsFriend);

  const handleMessage = () => {
    // Reaching a non-friend is only possible when they allow everyone, and that
    // opens as a request rather than a normal thread.
    const detail = { ...displayUser, isRequest: !derivedIsFriend && messagePref === 'everyone' };
    window.dispatchEvent(new CustomEvent('navigateToDirectMessage', { detail }));
  };

  const slapComboRef = useRef<number>(0);
  const slapTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSlap = () => {
    slapComboRef.current += 1;
    const currentCombo = slapComboRef.current;
    
    // 🔒 OPTIMISM: Increment slap count locally immediately
    adjustMetric(String(displayUser.id), 'user', 'slapCount', 1);

    toast.success(`Slapped ${displayUser.name}! 👋${currentCombo > 1 ? ` x${currentCombo}` : ''}`, { id: 'slap-toast', duration: 2000 });
    
    if (slapTimeoutRef.current) clearTimeout(slapTimeoutRef.current);
    
    slapTimeoutRef.current = setTimeout(async () => {
      const slapsToSend = slapComboRef.current;
      slapComboRef.current = 0; // reset
      if (slapsToSend === 0) return;

      try {
        const { UserService } = await import('../services/api');
        // Already incremented locally in handleSlap above (1 per click)
        await UserService.incrementSlaps(String(displayUser.id), slapsToSend);

        // 🔒 PERSIST LOCAL STATE: Update the base object so it doesn't fall back to 0 
        // when the MetricDelta STALE_TIMEOUT expires after 60 seconds.
        if (isSelf) {
          updateProfile({ slapCount: (userProfile.slapCount || 0) + slapsToSend });
        } else {
          setVisitorUser(prev => prev ? { ...prev, slapCount: (prev.slapCount || 0) + slapsToSend } : prev);
        }

        // 🔒 CLEANUP OPTIMISM: Now that the base state is updated, clear the session delta
        // to prevent "double counting" (Base + Delta) until the 60s timeout.
        resetMetric(String(displayUser.id), 'user', 'slapCount');

        const slapperName = userProfile?.name || firebaseUser?.displayName || 'Someone';
        await NotificationService.sendNotification(
          String(displayUser.id),
          'SLAP',
          `${slapperName} slapped you 👋`,
          `${slapsToSend} time${slapsToSend > 1 ? 's' : ''}!`,
          {
            fromUserId: firebaseUser?.uid,
            fromUserName: slapperName,
            fromUsername: (userProfile.username || '').replace(/@/g, ''),
            slapsBurst: slapsToSend
          }
        );
      } catch (error) {
        console.error('Error sending slap:', error);
      }
    }, 1500);
  };

  const handleRemoveFriendAction = async () => {
    try {
      if (window.confirm(`Are you sure you want to remove ${displayUser.name} as a friend?`)) {
        await UserService.removeFriend(String(userProfile.id), String(displayUser.id));
        setIsFriendState(false);
        setViewAsMode('nonfriend');
        toast.success("Friend removed");
      }
    } catch (e) {
      toast.error("Failed to remove friend");
    }
  };

  const handleBlockUserAction = async () => {
    try {
      if (window.confirm(`Are you sure you want to block ${displayUser.name}?`)) {
        // Remove from friends first (blockUser in context also does this, but
        // we explicitly call removeFriend here for belt-and-suspenders on the
        // local friends list state which blockUser doesn't update directly).
        await UserService.removeFriend(String(userProfile.id), String(displayUser.id));
        // Route through the context so blocking from a profile behaves exactly
        // like blocking from a yap: same friend teardown, same optimistic state
        // update, and the same auto-report signal (Apple Guideline 1.2). Calling
        // UserService.blockUser directly skipped the report.
        await contextBlockUser(String(displayUser.id));
        setIsFriendState(false);
        if (onBack) onBack();
      }
    } catch (e) {
      toast.error("Failed to block user");
    }
  };

  const handleReportUserAction = async () => {
    const reason = window.prompt(`Why are you reporting ${displayUser.name}? (Harassment, Impersonation, etc.)`);
    if (!reason || reason.trim().length < 4) {
      if (reason !== null) toast.error('A valid reason is required to report.');
      return;
    }
    try {
      const { reportToWatchers } = await import('../services/WatcherService');
      await reportToWatchers(
        String(userProfile.id),
        String(displayUser.id),
        'user',
        reason.trim(),
        {
          targetUserId: String(displayUser.id),
          reportedUsername: (displayUser as any).username || displayUser.name,
        }
      );
      toast.success('Report submitted. Our moderators will review this.');
    } catch (e) {
      toast.error('Failed to submit report.');
    }
  };

  // Mod-only (Legion/Watcher) account-hiding actions for portfolio violations.
  const viewerIsMod = ['legion', 'watcher'].includes(String((userProfile as any)?.rank || '').toLowerCase());
  const targetIsHidden = (displayUser as any)?.accountHidden === true;

  const handleHideAccountAction = async () => {
    if (!window.confirm(`This will hide ${displayUser.name}'s account from discovery and feeds. They will be notified to update their portfolio. Continue?`)) return;
    try {
      const { hideAccountForPortfolioViolation } = await import('../services/WatcherService');
      await hideAccountForPortfolioViolation(String(displayUser.id));
      toast.success(`${displayUser.name}'s account has been hidden.`);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to hide account.');
    }
  };

  const handleUnhideAccountAction = async () => {
    if (!window.confirm(`Restore ${displayUser.name}'s account? They will become visible in discovery again.`)) return;
    try {
      const { unhideAccount } = await import('../services/WatcherService');
      await unhideAccount(String(displayUser.id));
      toast.success(`${displayUser.name}'s account has been restored.`);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to restore account.');
    }
  };

  // Legion-only, matching adminDeleteAccount's server-side gate. Moderators
  // can't be deleted until their rank is removed, so the option isn't offered.
  const viewerIsLegion = String((userProfile as any)?.rank || '').toLowerCase() === 'legion';
  const targetIsMod = ['legion', 'watcher'].includes(String((displayUser as any)?.rank || '').toLowerCase());
  const canEmergencyDelete = viewerIsLegion && !isSelf && !targetIsMod;

  const handleEmergencyDeleteAction = async () => {
    const handle = String((displayUser as any)?.username || '').replace(/^@+/, '').toLowerCase();
    if (!handle) {
      toast.error('This account has no username to confirm against. Use the Legion panel.');
      return;
    }
    const reason = window.prompt(`Permanently delete @${handle}? Sign-in is disabled and the profile disappears immediately. This cannot be undone.\n\nReason (required for audit):`, 'Duplicate account');
    if (!reason || !reason.trim()) return;
    const typed = window.prompt(`Type ${handle} to confirm deletion.`);
    if (typed == null) return;
    if (typed.replace(/^@+/, '').trim().toLowerCase() !== handle) {
      toast.error('Username did not match. Nothing was deleted.');
      return;
    }
    try {
      await AdminService.deleteAccountAsModerator(String(displayUser.id), reason.trim());
      toast.success(`@${handle} is off the platform. Their content is being purged in the background.`);
      if (onBack) onBack();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to delete account.');
    }
  };

  const handleAddFriendAction = async () => {
    try {
      setIsProcessingRequest(true);

      // Charge gold: similar users cost 30, everyone else 10 (same as discover).
      const balance = userProfile.goldBalance ?? userProfile.gold ?? 0;
      if (balance < connectCost) {
        toast.error('Not enough gold', { description: `You need ${connectCost} gold to add this user.` });
        setIsProcessingRequest(false);
        return;
      }
      const econ = await EconomyService.deductGold(String(userProfile.id), connectCost, 'profile_add_friend');
      if (econ?.success && econ.newBalance !== undefined) {
        updateProfile({ goldBalance: econ.newBalance } as any);
      } else if (econ && econ.success === false) {
        toast.error('Not enough gold to add this user.');
        setIsProcessingRequest(false);
        return;
      }

      const result = await sendFriendRequest(
        String(displayUser.id),
        displayUser.name || '',
        displayUser.username || '',
        getUserAvatar(displayUser),
        calculateAge(displayUser.dateOfBirth) ?? undefined
      );

      // 🔁 MATCH DETECTION: If the other user had already sent us a request,
      // this is a mutual match — identical to swiping right on discover.
      if (result?.isMatch && result?.userData) {
        addNotification({
          type: 'friend_accepted',
          title: 'You are now new Friends!',
          message: `You and ${displayUser.name} are now connected. Start a conversation, slap them or stalk their yaps.`,
          userId: String(userProfile.id),
          userAvatar: getUserAvatar(displayUser),
          userName: displayUser.name || '',
          metadata: { friendId: String(displayUser.id) }
        });
        // Dispatch match event so MatchPopup renders (same as Discover)
        window.dispatchEvent(new CustomEvent('profileMatch', {
          detail: {
            ...displayUser,
            ...result.userData,
            age: calculateAge(displayUser.dateOfBirth) ?? undefined,
            image: getUserAvatar(displayUser),
          }
        }));
        // Update friend state immediately
        setIsFriendState(true);
        setViewAsMode('friend');
        // 📊 Optimistic count: bump their friend count by 1 so viewer sees it instantly
        adjustMetric(String(displayUser.id), 'user', 'friendsCount', 1);
        toast.success(`You and ${displayUser.name} are now friends! 🎉`);
      } else {
        toast.success("Friend request sent");
      }
    } catch (e) {
      console.error("[ProfileScreen] Error sending request:", e);
      // UserDataContext already shows toast.error if it's a context-level failure
      // but we add a fallback here just in case.
      if (!(e as any).handled) {
        toast.error("Failed to send request");
      }
    } finally {
      setIsProcessingRequest(false);
    }
  };

  const handleAcceptFriendAction = async () => {
    try {
      const request = friendRequests.find(r => r.fromUserId === String(displayUser.id));
      if (request) {
        await acceptFriendRequest(request.id);
        setIsFriendState(true);
        // 📊 Optimistic count: bump their friend count by 1 so viewer sees it instantly
        adjustMetric(String(displayUser.id), 'user', 'friendsCount', 1);
        toast.success("Friend request accepted");
      } else {
        toast.error("Request not found");
      }
    } catch (e) {
      console.error("[ProfileScreen] Error accepting request:", e);
      // We don't show another toast here because acceptFriendRequest in context already does
      setIsFriendState(false); // Rollback state
    }
  };

  const handleCancelRequestAction = async () => {
    try {
      setIsProcessingRequest(true);
      // Same refund as the preview sheet: which screen you cancel from must
      // not decide whether you get your gold back.
      await cancelFriendRequest(String(displayUser.id), connectCost);
    } catch (e) {
      console.error("[ProfileScreen] Error canceling request:", e);
    } finally {
      setIsProcessingRequest(false);
    }
  };

  const handleSetProfilePicture = (index: number) => {
    setCropType('profile');
    setSelectedImageIndex(index);
    setCropImage(userProfile.portfolio[index]);
    setShowCropModal(true);
  };

  const handleSaveCroppedImage = async (croppedImageUrl: string) => {
    if (cropType === 'banner') {
      setShowCropModal(false);
      setIsBannerUploading(true);
      try {
        const dataURLToBlob = (dataURL: string) => {
          const arr = dataURL.split(',');
          const mime = arr[0].match(/:(.*?);/)?.[1];
          const bstr = atob(arr[1]);
          let n = bstr.length;
          const u8arr = new Uint8Array(n);
          while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
          }
          return new Blob([u8arr], { type: mime });
        };
        const blob = dataURLToBlob(croppedImageUrl);
        const file = new File([blob], "banner.jpg", { type: "image/jpeg" });
        const bannerUrl = await uploadProfileBanner(String(userProfile.id), file);
        
        await handleBannerUpdate(bannerUrl);
        toast.success('Banner updated successfully!');
      } catch (error) {
        console.error('Error uploading banner:', error);
        toast.error('Failed to upload banner');
      } finally {
        setIsBannerUploading(false);
        setCropImage(null);
      }
      return;
    }

    if (selectedImageIndex !== null) {
      // ... (existing profile picture crop save logic)
      const currentPreviews = userProfile.portfolioPreviews || [];
      const newPreviews = userProfile.portfolio.map((url, idx) => {
        if (idx === selectedImageIndex) return croppedImageUrl;
        return currentPreviews[idx] || url;
      });

      updateProfile({
        profilePictureIndex: selectedImageIndex,
        croppedAvatar: croppedImageUrl,
        portfolioPreviews: newPreviews,
      });
      setShowCropModal(false);
      toast.success('Profile picture updated!');
    }
  };

  const handleSaveReorder = async () => {
    // Reconstruct portfolio and previews based on new order
    const newPortfolio = [...portfolioItems];
    const currentPortfolio = userProfile.portfolio || [];
    const currentPreviews = userProfile.portfolioPreviews || []; // Ensure we have the previews

    // Create mapping of URL -> Preview
    const urlToPreviewMap = new Map<string, string>();
    currentPortfolio.forEach((url, index) => {
      // Only map if we have a valid preview for this index
      if (currentPreviews[index]) {
        urlToPreviewMap.set(url, currentPreviews[index]);
      }
    });

    // Construct new previews array matching the new portfolio order
    const newPreviews = newPortfolio.map(url => urlToPreviewMap.get(url) || '');

    // Find the new index of the profile picture
    const currentProfilePicUrl = currentPortfolio[userProfile.profilePictureIndex || 0];
    const newProfilePicIndex = newPortfolio.indexOf(currentProfilePicUrl);

    await updateProfile({
      portfolio: newPortfolio,
      portfolioPreviews: newPreviews,
      profilePictureIndex: newProfilePicIndex !== -1 ? newProfilePicIndex : 0
    });

    setIsReordering(false);
    toast.success("Portfolio order saved");
  };


  const handleBannerUpdate = async (bannerUrl: string) => {
    await updateProfile({ bannerImage: bannerUrl, coverImage: bannerUrl });
  };

  const toggleViewAsMode = () => {
    if (viewAsMode === 'self') {
      setViewAsMode('friend');
      toast.info('Viewing profile as Friend');
    } else if (viewAsMode === 'friend') {
      setViewAsMode('nonfriend');
      toast.info('Viewing profile as Non-Friend');
    } else {
      setViewAsMode('self');
      toast.info('Viewing as Self');
    }
  };

  // 🔒 Friend Count Synchronization: Standardize filtering and avoid double-optimism
  const rawFriendsList = isSelf ? friends : visitorFriends;
  // Order friends by newest add (friendedAt = the friendship's createdAt). This
  // ordering flows to BOTH the profile preview strip and the View-All modal,
  // since both render `friendsList`. Friends missing the timestamp (legacy docs)
  // sort to the end.
  const friendedAtMs = (v: any): number => {
    if (!v) return 0;
    if (typeof v.toMillis === 'function') return v.toMillis();
    if (typeof v.seconds === 'number') return v.seconds * 1000;
    if (typeof v === 'number') return v;
    const d = new Date(v);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  };
  // Friendship date for the modal ("Friends since"). Empty when unknown.
  const formatFriendDate = (v: any): string => {
    const ms = friendedAtMs(v);
    if (!ms) return '';
    return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const friendsList = rawFriendsList
    .filter((f: any) =>
      f.name && f.name.trim() !== '' && f.name !== 'User' && f.setupComplete !== false
    )
    .sort((a: any, b: any) => friendedAtMs(b.friendedAt) - friendedAtMs(a.friendedAt));

  const friendsCount = isSelf 
    ? getFriendsCount() 
    : getOptimisticCount(friendsList.length, String(displayUser.id), 'user', 'friendsCount');

  // Merge shouted yaps with regular yaps and sort by timestamp
  const shoutedYaps = isSelf ? selfShoutedYaps : visitorShoutedYaps;
  const activeUserYaps = (userYaps || []).filter((yap: any) => !yap.deleted && !yap.is_hidden);
  
  const yapsCount = getOptimisticCount(activeUserYaps.length + (shoutedYaps || []).length, String(displayUser.id), 'user', 'yapsCount');

  const getAllYaps = () => {
    // Get pinned yaps from userYaps (excluding softly deleted ones)
    const pinnedYaps = activeUserYaps.filter((yap: any) => yap.isPinned);
    const pinnedYapIds = new Set(pinnedYaps.map((yap: Yap) => yap.id));

    const regularYaps = activeUserYaps
      .filter((yap: any) => !pinnedYapIds.has(String(yap.id))) // Exclude pinned yaps
      .map((yap) => {
        const sortTime = getTimestampMs(yap.timestamp);

        return {
          ...yap,
          type: 'regular' as const,
          displayTimestamp: yap.timestamp,
          sortTime
        };
      });

    const shoutedYapsFormatted = (shoutedYaps || []).map((shout) => ({
      ...shout,
      type: 'shouted' as const,
      displayTimestamp: shout.timestamp,
      sortTime: new Date(shout.timestamp).getTime(),
    }));

    // 🚫 A profile lists the owner's shouts, and a shout embeds someone else's
    // yap — so an unblocked user's profile could still surface a blocked
    // author's content through a shout of them. filterVisible checks the nested
    // originalYap author, not just the shouter.
    return filterVisible([...regularYaps, ...shoutedYapsFormatted]).sort((a, b) => {
      return b.sortTime - a.sortTime; // Sort descending (newest first)
    });
  };

  const allYaps = getAllYaps();

  const handleToggleShout = (yap: Yap, e: React.MouseEvent) => {
    e.stopPropagation();
    // Check if yap is already shouted
    if (isYapShouted(yap.id)) {
      setSelectedYapToShout(yap);
      setShowShoutOptionsModal(true);
    } else {
      setSelectedYapToShout(yap);
      setShowShoutModal(true);
    }
  };

  // This screen renders the user's own shouts from `shoutedYaps`, so it inserts
  // the new shout locally as well as calling toggleShout. That insert must wait
  // for the write to succeed: toggleShout rolls back its own state on failure,
  // but a locally added shout has no such rollback and would sit on the profile
  // as a phantom until the next refetch.
  const handleShoutWithCaption = async (caption: string, images: string[], friendsOnly: boolean, audioData?: { url: string, duration: number }) => {
    if (selectedYapToShout) {
      const ok = await toggleShout(selectedYapToShout.id, caption, images, audioData, friendsOnly);
      if (!ok) return;   // toggleShout has already surfaced the error
      const transformedYap = {
        ...selectedYapToShout,
        shouts: (selectedYapToShout.shouts || 0) + 1,
        showLocation: (selectedYapToShout as any).showLocation ?? true,
      };
      addShout(transformedYap, caption, images, audioData);
      toast.success(friendsOnly ? 'Yap shouted to friends only!' : 'Yap shouted with your caption!', { duration: 1500 });
    }
  };

  const handleShoutWithoutCaption = async () => {
    if (selectedYapToShout) {
      const ok = await toggleShout(selectedYapToShout.id);
      if (!ok) return;   // toggleShout has already surfaced the error
      const transformedYap = {
        ...selectedYapToShout,
        shouts: (selectedYapToShout.shouts || 0) + 1,
        showLocation: selectedYapToShout.showLocation ?? true,
      };
      addShout(transformedYap);
      toast.success('Yap shouted!', { duration: 1500 });
    }
  };

  const handleDeleteCaption = () => {
    if (selectedYapToShout) {
      updateShoutCaption(selectedYapToShout.id, undefined, undefined);
      toast.success('Caption deleted', { duration: 1500 });
    }
  };

  const handleDeleteShout = () => {
    if (selectedYapToShout) {
      toggleShout(selectedYapToShout.id);
      removeShout(selectedYapToShout.id);
      toast.success('Shout removed', { duration: 1500 });
    }
  };

  const handleViewAllYaps = () => {
    if (allYaps.length > 10) {
      if (isSelf) {
        window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_SCREEN, { detail: SCREENS.USER_YAPS }));
      } else {
        window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_FRIEND_YAPS, { detail: displayUser }));
      }
    } else {
      setIsYapsExpanded(!isYapsExpanded);
    }
  };

  // Theme Background Logic
  const getThemeBackground = () => {
    // Safety check: force default if not elite (though backend should enforce, UI should be safe)
    if (userProfile.subscriptionTier !== 'elite') return "bg-light-pattern dark:bg-[#0D0D0D]";

    switch (userProfile.profileBackgroundTheme) {
      case 'gold': return "bg-amber-50 dark:bg-amber-950";
      case 'royal_blue': return "bg-blue-50 dark:bg-blue-950/40";
      case 'burgundy': return "bg-rose-50 dark:bg-rose-950/40";
      case 'forest_green': return "bg-green-50 dark:bg-green-950/40";
      case 'sand': return "bg-orange-50 dark:bg-stone-900";
      case 'midnight': return "bg-slate-900 dark:bg-black";
      default: return "bg-light-pattern dark:bg-[#0D0D0D]";
    }
  };

  // 🚫 BLOCKED: this screen renders other users via /profile/:userId, and had a
  // privacy guard (isPrivateAccount) but no block guard — so a blocked user's
  // profile and yaps stayed reachable by URL. UserDetailModal already returns
  // null in this case; this is the same rule for the full-screen route.
  // Placed immediately before the single render return so every hook above still
  // runs unconditionally.
  // 🧭 WRONG-PROFILE GUARD: `displayUser` falls back to `userProfile` so the
  // render never crashes mid-load. The cost is that when someone else's profile
  // genuinely fails to resolve — a bad/legacy id, a permission error, a deleted
  // account — the fallback silently shows the VIEWER their own profile, which
  // reads exactly like "tapping their avatar took me to my page". Once loading
  // has finished with nothing fetched, say so instead of impersonating them.
  if (!isSelf && !visitorLoading && !visitorUser) {
    return (
      <div className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] flex items-center justify-center relative">
        <div className="text-center relative z-10 px-8">
          <p className="text-[#373737] dark:text-[#E5E5E5] mb-4">We couldn't load this profile.</p>
          <Button
            onClick={() => (onBack ? onBack() : undefined)}
            className="bg-brand-gradient text-white shadow-lg px-6 py-2 rounded-xl font-bold transition-all active:scale-95"
          >
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  if (!isSelf && isBlockedId(displayUser?.id)) {
    return (
      <div className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] flex items-center justify-center relative">
        <div className="text-center relative z-10 px-8">
          <p className="text-[#373737] dark:text-[#E5E5E5] mb-4">This profile isn't available.</p>
          <Button
            onClick={() => (onBack ? onBack() : undefined)}
            className="bg-brand-gradient text-white shadow-lg px-6 py-2 rounded-xl font-bold transition-all active:scale-95"
          >
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      animate={isShaking ? {
        x: [0, -10, 10, -10, 10, -5, 5, 0],
        y: [0, 5, -5, 5, -5, 0],
        rotate: [0, -1, 1, -1, 1, 0]
      } : { x: 0, y: 0, rotate: 0 }}
      transition={{ duration: 0.4 }}
      className={`w-screen h-screen relative flex flex-col overflow-hidden ${getThemeBackground()} transition-colors duration-500`}
    >
      <div className="absolute inset-0 pointer-events-none z-0">
        <ParallaxBackground speed={0.05} />
      </div>
      <div 
        ref={scrollContainerRef}
        key={`scroll-${displayUser.id}`}
        className="flex-1 min-h-0 w-full min-w-full overflow-y-auto overflow-x-hidden relative z-10"
      >
        <div className="w-full min-w-full max-w-2xl mx-auto pb-20">
          {(isLoading || visitorLoading) ? (
            <div className="w-full space-y-4">
              <SkeletonProfileHeader />
              <div className="px-6 space-y-4 pt-6">
                {Array.from({ length: 3 }).map((_, i) => <SkeletonYap key={i} />)}
              </div>
            </div>
          ) : (
            <>
              {/* Header with Cover */}
              <div className="relative h-48 bg-gray-200 dark:bg-gray-800 shadow-[0_4px_12px_rgba(0,0,0,0.15)] overflow-hidden">
            <img
              src={displayUser.bannerImage || DEFAULT_BANNER_URL}
              alt="Profile Banner"
              className="w-full h-full object-cover"
            />
            {isBannerUploading && (
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              </div>
            )}
            <div className="absolute inset-0 bg-linear-to-b from-black/10 to-transparent" />

            {!isSelf && (
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={onBack}
                style={{ 
                  top: 'max(60px, calc(env(safe-area-inset-top) + 24px))',
                  left: '24px'
                }}
                className="absolute w-10 h-10 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center hover:bg-black/60 transition-colors z-50 shadow-lg"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </motion.button>
            )}

          </div>

          {/* Reserved social-links strip — sits UNDER the banner.
              Two-layer technique to keep clicks reaching the icons even though
              the next "Profile Info" block uses -mt-16 to pull itself UP over
              this row:
                1. An empty 44px-tall spacer reserves vertical space so the
                   layout below NEVER shifts regardless of whether the user has
                   socials set.
                2. The actual SocialLinks render in an absolutely-positioned
                   wrapper with z-50 — it lives outside the normal flow, so the
                   Profile Info wrapper's z-10 stacking context can't swallow
                   its clicks no matter how the browser interprets stacking. */}
          <div className="relative" style={{ height: 44, marginTop: 12 }}>
            <div
              className="absolute inset-0 flex items-center justify-end px-6"
              style={{ zIndex: 50, pointerEvents: 'none' }}
            >
              <div style={{ pointerEvents: 'auto' }}>
                <SocialLinks
                  socials={(displayUser as any).socials}
                  size={32}
                  gap={10}
                  direction="row"
                  showHandle={false}
                />
              </div>
            </div>
          </div>

          {/* Businesses this person runs and has chosen to link from here.
              Renders nothing when there are none. */}
          <BusinessProfileBadges ownerId={String(displayUser.id || '')} />

          {/* Viewing As Indicator - Moved directly under banner */}
          {isSelf && viewAsMode !== 'self' && (
            <div className="px-6 pt-4">
              <div className="p-3 rounded-2xl bg-linear-to-r from-[#F39C12]/20 dark:from-[#4478ea]/20 to-[#E74C3C]/20 dark:to-[#2d1ef1]/20 border border-[#F39C12] dark:border-[#4478ea]">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#ea580c] dark:text-white">
                    Viewing as: <strong>{viewAsMode === 'friend' ? 'Friend' : 'Non-Friend'}</strong>
                  </span>
                  <Button
                    onClick={toggleViewAsMode}
                    variant="ghost"
                    className="text-[#F39C12] dark:text-[#4478ea] hover:text-[#E74C3C] dark:hover:text-[#2d1ef1]"
                  >
                    Exit Preview
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Profile Info.
              data-tour spans this whole block on purpose: the guide wants ONE
              spotlight covering the avatar down to and including the Edit /
              Share buttons, and this wrapper already has exactly those bounds,
              so no union of two rects is needed. */}
          <div
            data-tour="guide-profile-block"
            className={`w-full px-6 ${(isSelf && viewAsMode !== 'self') ? 'mt-4' : '-mt-16'} mb-6 relative z-10`}
          >
            <div className="relative inline-block mb-4">
              {/* Profile Picture */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="cursor-pointer relative z-20"
              >
                <LegionAvatarWrapper rank={displayUser.rank}>
                  <div className={`relative ${['legion'].includes(displayUser.rank as string) ? 'p-0' : 'p-1 rounded-full bg-white dark:bg-[#0D0D0D] shadow-2xl'} ${!['legion'].includes(displayUser.rank as string) ? getRankRingParams(displayUser.rank).ringClass : ''}`}>
                    <CinematicProfileAvatar
                      heroFrame={getUserAvatar(displayUser)}
                      videoUrl={displayUser.portfolioVideos?.[displayUser.profilePictureIndex ?? 0]}
                      trimStart={(displayUser.photoMetadata?.find((m: any) => Number(m.slot) === Number(displayUser.profilePictureIndex ?? 0)) as any)?.trimStart}
                      trimEnd={(displayUser.photoMetadata?.find((m: any) => Number(m.slot) === Number(displayUser.profilePictureIndex ?? 0)) as any)?.trimEnd}
                      size="lg"
                      isExpanded={isAvatarExpanded}
                      onExpand={() => setIsAvatarExpanded(!isAvatarExpanded)}
                      className="rounded-full"
                    />
                  </div>
                </LegionAvatarWrapper>
              </motion.div>

              {/* Real Flag — top-right corner of avatar, matching mobile/ios style */}
              {!isSystemModProfile && (displayUser.countryFlag || displayUser.country) && (
                <div 
                  className="absolute top-0 right-0 z-[1000] w-14 h-auto drop-shadow-2xl"
                  style={{ 
                    transform: 'translate(10%, -20%) rotate(15deg) translateZ(0)',
                    filter: 'drop-shadow(4px 4px 12px rgba(0,0,0,0.6))',
                    willChange: 'transform',
                    zIndex: 1000,
                  }}
                >
                  <FlagImage 
                    countryName={displayUser.countryFlag || displayUser.country} 
                    className="w-full h-auto"
                  />
                </div>
              )}
            </div>

            {/* Private Account Badge - shown when viewing as friend/non-friend and profile is private */}
            {viewAsMode !== 'self' && (displayUser as any).profileVisibility === 'private' && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#2C3E50]/20 dark:bg-[#2C3E50]/40 border border-[#2C3E50]/30 dark:border-[#2C3E50]/50 mb-3"
              >
                <Lock className="w-3.5 h-3.5 text-[#2C3E50] dark:text-[#E5E5E5]" />
                <span className="text-xs text-[#2C3E50] dark:text-[#E5E5E5]">Private Account</span>
              </motion.div>
            )}

            <h1 className="text-3xl font-bold text-black dark:text-white mb-1 tracking-tight flex items-center gap-2">
              <div className="flex items-center gap-2">
                <span>{displayUser.name?.replace(/\s+/g, ' ')}</span>
                {displayUser.verifiedBadge && <VerifiedBadge size={20} className="mb-0.5" />}
                {['legion', 'verified', 'watcher', 'union'].includes(displayUser.rank?.toLowerCase() || '') && (
                  <RankBadge rank={displayUser.rank} size={34} />
                )}
              </div>
            </h1>
            <div className="flex items-center gap-2 mb-4">
              <p className="text-sm font-normal text-[#666666] dark:text-[#a0a0a0] truncate">
                {`@${(displayUser.username || '').replace(/@/g, '')}`}
              </p>
              {/* An adult who has hidden their age still shows it to themselves,
                  so the setting is verifiable from their own profile. */}
              {!isSystemModProfile && ((displayUser as any).showAge !== false || isSelf) && calculateAge(displayUser.dateOfBirth) && (
                <span className="text-sm text-[#666666] dark:text-[#a0a0a0] flex items-center gap-2">
                  <span className="opacity-40 text-xl font-light">·</span>
                  {calculateAge(displayUser.dateOfBirth)}
                </span>
              )}
              {!isSelf && derivedIsFriend && <FriendBadge />}
              {!isSelf && !derivedIsFriend && isSimilarUser && <SimilarBadge />}
            </div>
            <div className="text-[#373737] dark:text-[#e0e0e0] mb-4 whitespace-pre-wrap">
              {displayUser.bio ? (
                <FormattedText text={displayUser.bio} className="block whitespace-pre-wrap" />
              ) : (
                'No bio yet.'
              )}
            </div>

            <div className="space-y-2 mb-6">
              {/* Location - Only name, no flag to avoid confusion with origin */}
              <div className="flex items-center gap-2 text-[#373737] dark:text-[#e0e0e0]">
                <MapPin className="w-4 h-4 text-[#F39C12] dark:text-[#4478ea]" />
                <span className="px-2 py-1 rounded-md border border-[#2C3E50]/30 dark:border-[#4478ea]/30 bg-[#2C3E50]/5 dark:bg-[#4478ea]/5">
                  {displayUser.showLocation === false ? 'Earth' : (displayUser.home_residence || displayUser.location || displayUser.country || "Location not set")}
                </span>
              </div>
              
              {displayUser.education && (
                <div className="flex items-center gap-2 text-[#373737] dark:text-[#e0e0e0]">
                  <GraduationCap className="w-4 h-4 text-[#F39C12] dark:text-[#4478ea]" />
                  <span className="px-2 py-1 rounded-md border border-[#2C3E50]/30 dark:border-[#4478ea]/30 bg-[#2C3E50]/5 dark:bg-[#4478ea]/5">{displayUser.education}</span>
                </div>
              )}
              {displayUser.occupation && (
                <div className="flex items-center gap-2 text-[#373737] dark:text-[#e0e0e0]">
                  <Briefcase className="w-4 h-4 text-[#F39C12] dark:text-[#4478ea]" />
                  <span className="px-2 py-1 rounded-md border border-[#2C3E50]/30 dark:border-[#4478ea]/30 bg-[#2C3E50]/5 dark:bg-[#4478ea]/5">{displayUser.occupation}</span>
                </div>
              )}
            </div>

            {/* Stats — hidden entirely for the system mod account */}
            {!isSystemModProfile && (
            <div className="flex flex-wrap gap-4 mb-6">
              {/* 🔒 Friends Privacy */}
              {!isPrivateAccount && displayUser.showFriendList !== false &&
               (displayUser as any).friendListVisibility !== 'private' && (
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea]" />
                  <span className="text-[#ea580c] dark:text-white">
                    <span className="font-bold">{formatCount(friendsCount)}</span>{' '}
                    <span className="text-[#666666] dark:text-[#a0a0a0]">friends</span>
                  </span>
                </div>
              )}

              {/* 🔒 Yaps Privacy */}
              {!isPrivateAccount && (displayUser as any).showYaps !== false && (
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea]" />
                  <span className="text-[#ea580c] dark:text-white">
                    <span className="font-bold">{formatCount(yapsCount)}</span>{' '}
                    <span className="text-[#666666] dark:text-[#a0a0a0]">yaps</span>
                  </span>
                </div>
              )}

              {/* 🔒 Slaps Privacy */}
              {!isPrivateAccount && (
                <div className="flex items-center gap-2">
                  <HandMetal className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea]" />
                  <span className="text-[#ea580c] dark:text-white">
                    <span className="font-bold">{formatCount(getOptimisticCount(displayUser.slapCount || 0, String(displayUser.id), 'user', 'slapCount'))}</span>{' '}
                    <span className="text-[#666666] dark:text-[#a0a0a0]">slaps</span>
                  </span>
                </div>
              )}
            </div>
            )}

            {/* View Profile As Toggle - Only shown in self mode */}
            {isSelf && viewAsMode === 'self' && (
              <div className="w-full mb-6 p-4 rounded-2xl bg-[#f2deb3] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10 card-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Eye className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea]" />
                    <span className="text-[#ea580c] dark:text-white">View Profile As</span>
                  </div>
                  <Button
                    onClick={toggleViewAsMode}
                    variant="outline"
                    className="bg-transparent border-2 border-[#F39C12] dark:border-[#4478ea] text-[#F39C12] dark:text-[#4478ea] hover:bg-[#F39C12] dark:hover:bg-[#4478ea]/20 dark:hover:border-[#2d1ef1] hover:text-white dark:hover:text-[#4478ea] rounded-xl button-shadow"
                  >
                    Toggle Preview
                  </Button>
                </div>
              </div>
            )}

            {/* Action Buttons based on Relationship */}
            <div className="flex w-full gap-3 mb-8" data-tour="guide-profile">
              {isSelf && viewAsMode === 'self' ? (
                <>
                  <Button
                    onClick={handleEditProfile}
                    className="flex-1 h-10 px-6 py-1.5 text-sm bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] hover:opacity-90 text-white rounded-xl button-shadow"
                  >
                    Edit Profile
                  </Button>
                  <Button
                    onClick={() => setShowShareModal(true)}
                    variant="outline"
                    className="flex-1 h-10 px-6 py-1.5 text-sm bg-[#f2deb3] dark:bg-[#1a1a1a] border-2 border-[#F39C12] dark:border-[#4478ea] text-[#F39C12] dark:text-white hover:bg-[#F39C12] dark:hover:bg-[#4478ea]/20 dark:hover:border-[#2d1ef1] hover:text-white dark:hover:text-[#4478ea] rounded-xl button-shadow"
                  >
                    Share Profile
                  </Button>
                </>
              ) : isSelf && (viewAsMode === 'friend' || viewAsMode === 'nonfriend') ? (
                // Self, but in "preview as friend/nonfriend" mode — show preview exit hint, not real buttons
                <Button onClick={toggleViewAsMode} variant="outline" className="flex-1 h-12 rounded-xl">
                  Exit Preview
                </Button>
              ) : derivedIsFriend ? (
                <>
                  {/* Message + activity centered beneath it (not under Slap).
                      Slap and the menu have fixed heights, so they stay top-aligned. */}
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <Button
                      onClick={handleMessage}
                      className="w-full h-10 px-6 py-1.5 text-sm bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] hover:opacity-90 text-white rounded-xl touch-target button-shadow"
                    >
                      Message
                    </Button>
                    <ActivityStatus userId={displayUser.id} live style={{ alignSelf: 'center', marginTop: 32, marginBottom: -24 }} />
                  </div>
                  <Button 
                    onClick={handleSlap} 
                    className="h-10 px-6 min-w-[140px] py-1.5 text-sm font-bold shrink-0 bg-[#E74C3C] dark:bg-linear-to-r dark:from-[#4478ea] dark:to-[#2d1ef1] hover:bg-[#c0392b] dark:hover:opacity-90 text-white rounded-xl touch-target button-shadow"
                  >
                    Slap 👋
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-12 px-4 hover:bg-black/5 dark:hover:bg-[#eddeb7]/5">
                        <MoreVertical className="w-5 h-5 text-[#666666] dark:text-[#e0e0e0]" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-[#eddeb7] dark:bg-[#2C3E50] border border-black/10 dark:border-white/10">
                      <DropdownMenuItem onClick={handleRemoveFriendAction} className="text-[#E74C3C] dark:text-[#DC2626] cursor-pointer">
                        <UserMinus className="w-4 h-4 mr-2" />
                        Remove Friend
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleBlockUserAction} className="text-[#E74C3C] dark:text-[#DC2626] cursor-pointer">
                        <Ban className="w-4 h-4 mr-2" />
                        Block User
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={handleReportUserAction} className="text-[#E74C3C] dark:text-[#DC2626] cursor-pointer">
                        <Megaphone className="w-4 h-4 mr-2" />
                        Report User
                      </DropdownMenuItem>
                      {viewerIsMod && !isSelf && (
                        targetIsHidden ? (
                          <DropdownMenuItem onSelect={handleUnhideAccountAction} className="text-green-600 dark:text-green-400 cursor-pointer">
                            <Eye className="w-4 h-4 mr-2" />
                            Unhide Account
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onSelect={handleHideAccountAction} className="text-orange-600 dark:text-orange-400 cursor-pointer">
                            <EyeOff className="w-4 h-4 mr-2" />
                            Hide Account (Portfolio Violation)
                          </DropdownMenuItem>
                        )
                      )}
                      {canEmergencyDelete && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onSelect={handleEmergencyDeleteAction} className="text-[#E74C3C] dark:text-[#DC2626] font-semibold cursor-pointer">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Account (Emergency)
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <>
                  {/* Connect/Message group + activity centered beneath it. */}
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                  <div className="flex gap-3 flex-1 w-full">
                    {/* Show Accept if there's a pending incoming request from this user */}
                    {(hasPendingRequestFrom(String(displayUser.id)) || 
                      friendRequests.some(r => 
                        String(r.fromUserId) === String(displayUser.id) && 
                        r.status !== 'accepted' && 
                        r.status !== 'rejected'
                      )
                    ) ? (
                      <Button 
                        onClick={handleAcceptFriendAction} 
                        className="flex-1 h-12 hover:opacity-90 text-white rounded-xl touch-target button-shadow border-none"
                        style={{ background: 'linear-gradient(to right, #2ECC71, #27AE60)' }}
                      >
                        <UserCheck className="w-5 h-5 mr-2" />
                        Accept
                      </Button>
                    ) : hasPendingRequestTo(String(displayUser.id)) ? (
                      <Button
                        onClick={handleCancelRequestAction}
                        disabled={isProcessingRequest}
                        className="flex-1 h-12 rounded-xl touch-target border-2 border-[#E74C3C] dark:border-[#DC2626] text-[#E74C3C] dark:text-[#DC2626] bg-transparent hover:bg-[#E74C3C]/10 dark:hover:bg-[#DC2626]/10 disabled:opacity-50"
                        variant="outline"
                      >
                        {isProcessingRequest ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <X className="w-5 h-5 mr-2" />
                            Cancel
                          </>
                        )}
                      </Button>
                    ) : isSystemModProfile ? null : (
                      <Button
                        onClick={handleAddFriendAction}
                        disabled={isProcessingRequest}
                        className="flex-1 h-12 bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] hover:opacity-90 text-white rounded-xl touch-target button-shadow border-none disabled:opacity-50"
                      >
                        {isProcessingRequest ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            {/* No mr-2: the Button base already sets gap-2, so the
                                margin stacked on top and pushed the icon away. */}
                            <UserPlus className="w-5 h-5 shrink-0" />
                            <span className="text-xs sm:text-sm whitespace-nowrap">Connect · {connectCost} Gold</span>
                          </>
                        )}
                      </Button>
                    )}

                    {canMessage && (
                      <Button
                        onClick={handleMessage}
                        variant="outline"
                        className="flex-1 h-12 rounded-xl touch-target border-2 border-[#4478ea] text-[#4478ea] dark:text-white hover:bg-[#4478ea]/10 dark:hover:bg-[#4478ea]/20 dark:border-[#4478ea]"
                      >
                        <MessageSquare className="w-5 h-5 mr-2" />
                        Message
                      </Button>
                    )}
                  </div>
                  <ActivityStatus userId={displayUser.id} live style={{ alignSelf: 'center', marginTop: 32, marginBottom: -24 }} />
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-12 px-4 hover:bg-black/5 dark:hover:bg-[#eddeb7]/5">
                        <MoreVertical className="w-5 h-5 text-[#666666] dark:text-[#e0e0e0]" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-[#eddeb7] dark:bg-[#2C3E50] border border-black/10 dark:border-white/10">
                      <DropdownMenuItem onClick={handleBlockUserAction} className="text-[#E74C3C] dark:text-[#DC2626] cursor-pointer">
                        <Ban className="w-4 h-4 mr-2" />
                        Block User
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleReportUserAction} className="text-[#E74C3C] dark:text-[#DC2626] cursor-pointer">
                        <Megaphone className="w-4 h-4 mr-2" />
                        Report User
                      </DropdownMenuItem>
                      {viewerIsMod && !isSelf && (
                        targetIsHidden ? (
                          <DropdownMenuItem onClick={handleUnhideAccountAction} className="text-green-600 dark:text-green-400 cursor-pointer">
                            <Eye className="w-4 h-4 mr-2" />
                            Unhide Account
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={handleHideAccountAction} className="text-orange-600 dark:text-orange-400 cursor-pointer">
                            <EyeOff className="w-4 h-4 mr-2" />
                            Hide Account (Portfolio Violation)
                          </DropdownMenuItem>
                        )
                      )}
                      {canEmergencyDelete && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={handleEmergencyDeleteAction} className="text-[#E74C3C] dark:text-[#DC2626] font-semibold cursor-pointer">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Account (Emergency)
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
            </div>

            {/* Privacy Wall Notification */}
            {isPrivateAccount && (
              <div className="mb-8 p-8 rounded-3xl bg-white/5 border-2 border-dashed border-[#4478ea]/30 flex flex-col items-center text-center gap-4">
                <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center shadow-lg">
                  <Lock className="w-8 h-8 text-[#4478ea]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#4478ea] dark:text-white mb-2">This Profile is Private</h3>
                  <p className="text-[#666666] dark:text-[#a0a0a0] max-w-[280px]">
                    You must be friends with this user in order to see their profile activity.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Portfolio */}
          <div className="px-6 mb-8">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <h2 className="text-xl text-black dark:text-white font-bold tracking-tight">Portfolio</h2>
              {isSelf && (
                isReordering ? (
                  <div className="flex gap-2">
                    <Button
                      onClick={handleSaveReorder}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white rounded-full px-4"
                    >
                      Save Order
                    </Button>
                    <Button
                      onClick={() => { setIsReordering(false); setPortfolioItems((userProfile.portfolio || [])); }}
                      size="sm"
                      variant="ghost"
                      className="text-red-500 hover:bg-red-50 rounded-full px-4"
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={() => { setIsReordering(true); }}
                    variant="outline"
                    className="bg-transparent border-2 border-[#4478ea] text-[#4478ea] hover:bg-[#4478ea]/20 dark:hover:border-[#2d1ef1] hover:text-white dark:hover:text-[#4478ea] rounded-xl flex items-center gap-2 button-shadow"
                  >
                    <Edit className="w-4 h-4" />
                    Drag to Reorganize
                  </Button>
                )
              )}
            </div>

            {isReordering ? (
              // Render as a div (not the default <ul>) so browser-default
                            //   padding-inline-start: 40px doesn't shrink the grid columns,
              // and skip the outer wrapper that previously double-nested grids.
              <Reorder.Group
                as="div"
                axis="x"
                values={portfolioItems.filter(p => p && p.length > 0)}
                onReorder={(newOrder) => {
                  const padded = [...newOrder];
                  while (padded.length < 4) padded.push("");
                  setPortfolioItems(padded);
                }}
                className="grid grid-cols-4 gap-3"
                style={{ listStyle: 'none', padding: 0, margin: 0 }}
              >
                {portfolioItems.map((image, index) => {
                  const originalIndex = (displayUser.portfolio || [])?.indexOf(image) ?? -1;
                  const preview = originalIndex !== -1 ? displayUser.portfolioPreviews?.[originalIndex] : image;
                  const isEmpty = !image || image.length === 0;

                  if (isEmpty) {
                    return (
                      <div key={`static-${index}`} className="relative aspect-3/4 rounded-2xl overflow-hidden opacity-20 border border-black/5 dark:border-white/5 bg-[#eddeb7] dark:bg-[#1a1a1a] flex items-center justify-center">
                         <X className="w-8 h-8 text-black dark:text-white" />
                      </div>
                    );
                  }

                  return (
                    <Reorder.Item
                      as="div"
                      key={image}
                      value={image}
                      className="relative aspect-3/4 rounded-2xl overflow-hidden touch-none cursor-grab active:cursor-grabbing shadow-sm border border-black/5 dark:border-white/5 bg-[#eddeb7] dark:bg-[#1a1a1a]"
                      style={{ listStyle: 'none' }}
                    >
                      {preview || image ? (
                        <img
                          src={preview || image}
                          alt={`Portfolio ${index + 1}`}
                          className="w-full h-full object-cover pointer-events-none"
                          draggable={false}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center opacity-20">
                          <X className="w-8 h-8 text-black dark:text-white" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/20 flex items-center justify-center pointer-events-none">
                        <Edit className="w-6 h-6 text-white opacity-70" />
                      </div>
                    </Reorder.Item>
                  );
                })}
              </Reorder.Group>
            ) : (
              <div className="grid grid-cols-4 gap-3">
                {Array.from({ length: 4 }).map((_, index) => {
                  const rawImg = (displayUser.portfolioPreviews?.[index] || displayUser.portfolio?.[index] || '').trim();
                  const videoUrl = (displayUser.portfolioVideos?.[index] || (isMaybeVideo(rawImg) ? rawImg : '')).trim();
                  const displayImage = (!isMaybeVideo(rawImg) ? rawImg : "") || (videoUrl ? (displayUser.image || displayUser.avatar) : "");

                  return (
                    <motion.div
                      key={index}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => (displayImage || videoUrl) && setViewingPortfolioIndex(index)}
                      className="relative aspect-3/4 rounded-2xl overflow-hidden cursor-pointer group shadow-sm border border-black/5 dark:border-white/5 bg-[#eddeb7] dark:bg-[#1a1a1a]"
                    >
                      {displayImage ? (
                        <div className="relative w-full h-full">
                          <img
                            src={displayImage}
                            alt={`Portfolio ${index + 1}`}
                            className="w-full h-full object-cover object-center"
                            loading="lazy"
                          />
                          {videoUrl && (
                             <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                                <Play className="w-8 h-8 text-white fill-current opacity-70" />
                             </div>
                          )}
                        </div>
                      ) : (
                        <div 
                          className="w-full h-full flex items-center justify-center opacity-20"
                        >
                          {videoUrl ? (
                            <Play className="w-8 h-8 text-black dark:text-white fill-current" />
                          ) : (
                            <X className="w-8 h-8 text-black dark:text-white" />
                          )}
                        </div>
                      )}

                      {/* Avatar Badge - Original semi-transparent pill style */}
                      {isSelf && index === displayUser.profilePictureIndex && (
                        <div className="absolute bottom-2 right-2 bg-black/60 backdrop-blur-md py-0.5 px-2 rounded-full border border-white/20 shadow-lg flex items-center justify-center">
                          <span className="text-[6px] font-bold text-white tracking-wider uppercase">Avatar</span>
                        </div>
                      )}

                      {/* Video Indicator / Eye Overlay */}
                      {(displayImage || videoUrl) && (
                        <div 
                          className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                        >
                          {videoUrl ? (
                            <Play className="w-8 h-8 text-white fill-current opacity-70" />
                          ) : (
                            <Eye className="w-6 h-6 text-white opacity-70" />
                          )}
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>

          {/* My Yaps Section - Hidden if private and not friend, or system mod account */}
          {!isPrivateAccount && !isSystemModProfile && (
            <div className="px-6 mb-8">
              {/* Pinned Yaps Section */}
              {(() => {
                const pinnedYapsArray = (userYaps || []).filter(yap => yap.isPinned);
                if (pinnedYapsArray.length > 0) {
                  return (
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Pin className="w-5 h-5 text-[#4478ea]" />
                        <h3 className="text-lg text-[#4478ea] dark:text-white">Pinned Yaps</h3>
                      </div>
                      <div className="space-y-3">
                        {pinnedYapsArray.map((yap: Yap) => (
                          <PressableCard
                            key={yap.id}
                            onClick={() => {
                              saveScrollPosition();
                              // Open the standard Yap Details (same as every other yap).
                              // Must dispatch the yap itself as the detail — the router
                              // handler reads detail.id, so wrapping it in an object made
                              // the tap a no-op.
                              window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_YAP_DETAIL, { detail: yap }));
                            }}
                            className="p-4 rounded-2xl bg-white/5 border-2 border-[#4478ea] relative cursor-pointer hover:shadow-md transition-shadow"
                          >
                            {/* Pinned badge */}
                            <div className="absolute top-3 right-3 z-10">
                              <div className="bg-[#4478ea] p-1.5 rounded-lg">
                                <Pin className="w-3 h-3 text-white" />
                              </div>
                            </div>

                            {/* Standardized Header for Pinned Yap */}
                            <div className="flex items-center gap-1.5 mb-3 flex-wrap pr-10">
                              <span className="text-sm font-bold text-black dark:text-white truncate">
                                @{(yap?.username || 'unknown').replace(/@/g, '').toLowerCase()}
                              </span>
                              {displayUser.rank && ['watcher', 'legion', 'verified', 'union'].includes(displayUser.rank) && (
                                <RankBadge rank={displayUser.rank} size={14} />
                              )}
                              <FlagImage countryName={displayUser.countryFlag || displayUser.country} size="w80" className="w-4 aspect-3/2 object-contain rounded" />
                              <span className="text-xs text-[#666666] dark:text-[#a0a0a0]">•</span>
                              <div className="flex items-center gap-1 shrink-0">
                                <MapPin style={{ width: '10px', height: '10px' }} className="text-[#4478ea] shrink-0" />
                                <span className="text-xs text-[#666666] dark:text-[#a0a0a0]">{displayUser.showLocation === false ? 'Earth' : (displayUser.home_residence || displayUser.location || displayUser.country || "Earth")}</span>
                              </div>
                            </div>
                            <p className="text-[#333333] dark:text-[#e0e0e0] mb-3 pr-10 whitespace-pre-wrap wrap-break-word">{yap.content}</p>
                            
                            {yap.audioUrl && (
                              <div className="mb-3 px-2">
                                <VoiceReplyBubble 
                                  audioUrl={yap.audioUrl} 
                                  duration={yap.duration}
                                  className="w-full"
                                />
                              </div>
                            )}

                            {/* Images if exists */}
                            {(yap.image || (yap.images && yap.images.length > 0)) && (
                              <div className={`mb-4 gap-2 ${(yap.images && yap.images.length >= 2) || (yap.image && yap.images && yap.images.length >= 1) ? 'grid grid-cols-2' : 'grid grid-cols-1'}`}>
                                {yap.images && yap.images.length > 0 ? (
                                  yap.images.slice(0, 2).map((img, idx) => (
                                    <div key={idx} className="aspect-video rounded-xl overflow-hidden border border-black/5 dark:border-white/10 bg-black/5 dark:bg-white/5">
                                      <ImageWithPreview
                                        src={img}
                                        alt={`Yap content ${idx + 1}`}
                                        className="w-full h-full object-cover"
                                      />
                                    </div>
                                  ))
                                ) : (
                                  <div className="rounded-xl overflow-hidden border border-black/5 dark:border-white/10 bg-black/5 dark:bg-white/5 w-fit">
                                    <ImageWithPreview
                                      src={yap.image!}
                                      alt="Yap content"
                                      className="object-contain"
                                      style={{ maxHeight: '280px', width: 'auto', height: 'auto' }}
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                            <div className="flex items-center gap-3">
                              <LikeButton
                                postId={yap.id}
                                postType="yap"
                                originalLikeCount={yap.likes || 0}
                                canInteract={() => true}
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                className="flex items-center gap-2 h-auto px-3 py-1.5 text-[#666666] dark:text-[#E5E5E5] hover:text-[#4478ea]"
                              >
                                <MessageSquare className="w-4 h-4" />
                                <span className="text-sm">{yap.comments}</span>
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  if (isYapShouted(yap.id)) {
                                    toggleShout(yap.id);
                                    removeShout(yap.id);
                                  } else {
                                    toggleShout(yap.id);
                                    addShout({
                                      ...yap,
                                      location: userProfile?.showLocation === false ? 'Earth' : (userProfile?.home_residence || userProfile?.location || userProfile?.country || 'Earth')
                                    }, undefined, undefined);
                                  }
                                }}
                                className={`flex items-center gap-2 h-auto px-3 py-1.5 ${isYapShouted(yap.id)
                                  ? 'text-[#4478ea]'
                                  : 'text-[#666666] dark:text-[#E5E5E5] hover:text-[#4478ea]'
                                  } `}
                              >
                                <Megaphone className="w-4 h-4" />
                                <span className="text-sm">{getShoutCount(yap.shouts || 0, yap.id)}</span>
                              </Button>

                              <span className="ml-auto shrink-0 min-w-max text-sm text-[#999999] dark:text-[#808080]">{formatTimeAgo(yap.time || yap.timestamp || 'Just now')}</span>
                            </div>
                          </PressableCard>
                        ))}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xl text-[#4478ea] dark:text-white">
                  {viewAsMode === 'self' ? 'My Yaps' : 'Yaps'}
                </h2>
                {allYaps.length > 3 && (
                  <Button
                    onClick={handleViewAllYaps}
                    variant="ghost"
                    className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[#4478ea] dark:text-white hover:bg-white/10 transition-colors card-shadow text-xs font-bold uppercase tracking-widest"
                  >
                    {isYapsExpanded ? 'Show Less' : 'View All →'}
                  </Button>
                )}
              </div>

              {/* Yaps List */}
              {(() => {
                // 🔒 PRIVACY: Hide yaps from visitors if the user has explicitly disabled visibility.
                const showYapsToggle = (displayUser as any).showYaps !== false; // Default: true
                if (!isSelf && !showYapsToggle) {
                  return (
                    <div className="w-full flex flex-col items-center justify-center py-12 px-6 rounded-2xl bg-black/5 dark:bg-white/5 border border-dashed border-black/10 dark:border-white/10">
                      <div className="w-12 h-12 rounded-full bg-orange-500/10 dark:bg-blue-500/10 flex items-center justify-center mb-4">
                        <EyeOff className="w-6 h-6 text-orange-500 dark:text-blue-500" />
                      </div>
                      <p className="text-sm font-bold text-black dark:text-white">Yaps are hidden</p>
                      <p className="text-xs text-[#666666] dark:text-[#a0a0a0] mt-1 text-center">
                        This user has chosen to keep their yap feed private.
                      </p>
                    </div>
                  );
                }

                if (allYaps.length === 0) {
                  return (
                    <div className="w-full text-center py-8 text-[#666666] dark:text-[#a0a0a0]">
                      <p className="text-sm">No yaps yet</p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-3">
                    {allYaps.slice(0, isYapsExpanded ? undefined : 3).map((item) => {
                      const authorInfo = getYapAuthorInfo(item, userProfile);
                      const interactionId = getYapInteractionId(item);
                      const isQuote = isQuoteShoutUtil(item);
                      const isSimple = item.type === 'shouted' && !isQuote;

                      // Use original content for simple shouts
                      const displayYap = isSimple ? (item as any).originalYap : item;

                      return (
                        <PressableCard
                          key={item.id}
                          onClick={() => {
                            if (item.type === 'shouted') {
                              saveProfileScrollPosition();
                              window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_SCREEN, { detail: { screen: 'shoutDetail', yap: item } }));
                            } else {
                              saveProfileScrollPosition();
                              window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_YAP_DETAIL, { detail: item }));
                            }
                          }}
                          className="p-4 rounded-2xl bg-[#f2deb3] dark:bg-[#1a1a1a] border border-black/10 dark:border-white/10 cursor-pointer hover:shadow-md transition-shadow relative"
                        >
                          {/* Shouted Header (X-style) */}
                          {isSimple && (
                            <div className="flex items-center gap-2 mb-2 ml-4">
                              <Megaphone className="w-3 h-3 text-[#8e8e8e] dark:text-[#a0a0a0] fill-current" />
                              <span className="text-[13px] font-bold text-[#8e8e8e] dark:text-[#a0a0a0]">
                                {item.shoutedByUsername === userProfile.username ? 'You Shouted' : `${item.shoutedByName} Shouted`}
                              </span>
                            </div>
                          )}



                          <div className="flex gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between mb-2">
                                <div className="min-w-0">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="text-sm font-bold text-black dark:text-white truncate">
                                      {`@${(authorInfo.username || '').replace(/^@/, '')}` || `@${authorInfo.name.toLowerCase().replace(/\s+/g, '')}`}
                                    </span>
                                    {authorInfo.rank && ['watcher', 'legion', 'verified', 'union'].includes(authorInfo.rank.toLowerCase()) && (
                                      <RankBadge rank={authorInfo.rank} size={14} />
                                    )}
                                    <span className="text-xs text-[#666666] dark:text-[#a0a0a0]">•</span>
                                    <span className="text-xs text-[#666666] dark:text-[#a0a0a0]">{formatTimeAgo((item as any).displayTimestamp || (item as any).timestamp || 'Just now')}</span>
                                  </div>
                                </div>
                              </div>


                              {/* Simple shout: show original yap via EmbeddedYap */}
                              {isSimple && (item as any).originalYap && (
                                <div className="mt-1">
                                  <EmbeddedYap
                                    yapId={getOriginalYapId(item)}
                                    username={(item as any).originalYap.username || (item as any).originalYap.userUsername || '@unknown'}
                                    authorName={(item as any).originalYap.author || (item as any).originalYap.userName || 'Unknown'}
                                    avatar={(item as any).originalYap.avatar || (item as any).originalYap.userAvatar || ''}
                                    country={(item as any).originalYap.countryFlag || (item as any).originalYap.country}
                                    timestamp={(item as any).originalYap.timestamp || (item as any).originalYap.time || ''}
                                    content={(item as any).originalYap.content || ''}
                                    images={(item as any).originalYap.images || ((item as any).originalYap.image ? [(item as any).originalYap.image] : [])}
                                    image={(item as any).originalYap.image}
                                    likes={(item as any).originalYap.likes || 0}
                                    comments={(item as any).originalYap.comments || 0}
                                    shouts={(item as any).originalYap.shouts || 0}
                                    isLiked={isYapLiked(getOriginalYapId(item))}
                                    isShout={true}
                                    isDeleted={(item as any).originalYap?.deleted}
                                    showActions={false}
                                    onYapClick={(e) => {
                                      e?.stopPropagation();
                                      window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_YAP_DETAIL, { detail: (item as any).originalYap }));
                                    }}
                                    maxLines={3}
                                    minimal={true}
                                  />
                                </div>
                              )}

                              {/* Quote Shout Caption */}
                              {isQuote && (
                                <p className="text-[#666666] dark:text-[#E5E5E5] mb-2 text-[15px] leading-relaxed whitespace-pre-wrap wrap-break-word">{(item as any).shoutCaption}</p>
                              )}

                              {/* Voice Bubble (Shout Audio or Yap Audio) */}
                              {((item as any).audioUrl || displayYap.audioUrl) && (
                                <div className="mb-3">
                                  <VoiceReplyBubble 
                                    audioUrl={(item as any).audioUrl || displayYap.audioUrl} 
                                    duration={(item as any).duration || displayYap.duration}
                                    className="w-full"
                                  />
                                </div>
                              )}

                              {/* Main Content (for regular yaps) */}
                              {!isQuote && !isSimple && (
                                <p className="text-[#666666] dark:text-[#E5E5E5] mb-3 text-[15px] leading-relaxed whitespace-pre-wrap wrap-break-word">{(displayYap as any).content}</p>
                              )}

                              {/* Images (Shout Images or Yap Images) */}
                              {isQuote && (item as any).shoutImages && (item as any).shoutImages.length > 0 ? (
                                <div className={`mb-3 gap-2 ${(item as any).shoutImages.length >= 2 ? 'grid grid-cols-2' : 'grid grid-cols-1'}`}>
                                  {(item as any).shoutImages.map((img: string, idx: number) => (
                                    <div key={idx} className="aspect-video rounded-xl overflow-hidden border border-black/5 dark:border-white/10 bg-black/5 dark:bg-white/5">
                                      <ImageWithPreview
                                        src={img}
                                        alt={`Shout image ${idx + 1}`}
                                        className="w-full h-full object-cover"
                                      />
                                    </div>
                                  ))}
                                </div>
                              ) : (!isSimple && (displayYap as any).image && (
                                <div className="mb-3 rounded-xl overflow-hidden border border-black/5 dark:border-white/10 bg-black/5 dark:bg-white/5 w-fit">
                                  <ImageWithPreview
                                    src={(displayYap as any).image}
                                    alt="Yap content"
                                    className="object-contain"
                                    style={{ maxHeight: '300px', width: 'auto', height: 'auto' }}
                                  />
                                </div>
                              ))}

                              {/* Embedded Yap for Quote Shouts */}
                              {isQuote && (item as any).originalYap && (
                                <div className="mt-1">
                                  <EmbeddedYap
                                    yapId={(item as any).originalYap.id}
                                    username={(item as any).originalYap.username || (item as any).originalYap.userUsername || '@unknown'}
                                    authorName={(item as any).originalYap.author || (item as any).originalYap.userName || 'Unknown'}
                                    avatar={(item as any).originalYap.avatar || (item as any).originalYap.userAvatar || ''}
                                    country={(item as any).originalYap.countryFlag || (item as any).originalYap.country}
                                    timestamp={(item as any).originalYap.timestamp || (item as any).originalYap.time || ''}
                                    content={(item as any).originalYap.content || ''}
                                    images={(item as any).originalYap.images || ((item as any).originalYap.image ? [(item as any).originalYap.image] : [])}
                                    image={(item as any).originalYap.image}
                                    likes={(item as any).originalYap.likes || 0}
                                    comments={(item as any).originalYap.comments || 0}
                                    shouts={(item as any).originalYap.shouts || 0}
                                    isLiked={isYapLiked((item as any).originalYap.id, 'yap')}
                                    isShout={true}
                                    isDeleted={(item as any).originalYap?.deleted}
                                    showActions={false}
                                    showLocation={(item as any).originalYap.showLocation ?? true}
                                    onYapClick={(e) => {
                                      e?.stopPropagation();
                                      window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_YAP_DETAIL, { detail: (item as any).originalYap }));
                                    }}
                                    maxLines={3}
                                    minimal={true}
                                  />
                                </div>
                              )}

                              {/* Action Bar */}
                              <div className="flex items-center gap-3 mt-1">
                                <LikeButton
                                  postId={interactionId}
                                  postType={getYapPostType(item)}
                                  originalLikeCount={(item as any).likes || 0}
                                  canInteract={() => {
                                    const tId = (item as any).tribeId;
                                    const fId = (item as any).forumId;
                                    if (tId && !isTribeMember(tId)) return false;
                                    if (fId && !isForumMember(fId)) return false;
                                    return true;
                                  }}
                                  onBlockedInteraction={() => {
                                    toast.error("Members only", { description: "Join this group to like yaps." });
                                  }}
                                />
                              {/* Shout button ONLY for non-shouts and non-group yaps */}
                              {!isQuote && !isSimple && !(item as any).tribeId && !(item as any).forumId && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    if ((item as any).tribeId && !isTribeMember((item as any).tribeId)) {
                                        toast.error("Members only", { description: "Join this tribe to shout." });
                                        return;
                                    }
                                    if ((item as any).forumId && !isForumMember((item as any).forumId)) {
                                        toast.error("Members only", { description: "Join this forum to shout." });
                                        return;
                                    }
                                    handleToggleShout(item as any, e);
                                  }}
                                  className={`flex items-center gap-2 h-auto px-3 py-1.5 ${((item as any).tribeId && !isTribeMember((item as any).tribeId)) || ((item as any).forumId && !isForumMember((item as any).forumId)) ? 'opacity-50' : isYapShouted(interactionId)
                                    ? 'text-[#F39C12] dark:text-[#4478ea]'
                                    : 'text-[#666666] dark:text-[#E5E5E5] hover:text-[#F39C12] dark:hover:text-[#4478ea]'
                                    } `}
                                >
                                  <Megaphone className={`w-4 h-4 ${isYapShouted(interactionId) ? 'fill-[#F39C12]' : ''} `} />
                                  <span className="text-sm">{getShoutCount((item as any).shouts || 0, interactionId)}</span>
                                </Button>
                              )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="flex items-center gap-2 h-auto px-3 py-1.5 text-[#666666] dark:text-[#E5E5E5]"
                                >
                                  <MessageSquare className="w-4 h-4" />
                                  <span className="text-sm">{(item as any).replies || (item as any).comments || 0}</span>
                                </Button>
                                <span className="ml-auto shrink-0 min-w-max text-sm text-[#999999] dark:text-[#808080]">{formatTimeAgo((item as any).displayTimestamp || (item as any).timestamp || 'Just now')}</span>
                              </div>
                            </div>
                          </div>
                        </PressableCard>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Identity — hidden for the system mod account */}
          {!isSystemModProfile && (
          <div className="px-6 mb-8">
            <h2 className="text-xl text-[#ea580c] dark:text-white mb-3">Identity</h2>
            <div className="flex flex-wrap gap-3">
              {(displayUser.identities || []).map((identity, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <IdentityTag tag={identity} variant="profile" />
                  </motion.div>
              ))}
            </div>
          </div>
          )}

          {/* Friends */}
          {(() => {
            // Social Privacy Rules:
            // 1. Owners (isSelf) always see their own list.
            // 2. If showFriendList is explicitly false, hide from EVERYONE else.
            // 3. Otherwise, respect the visibility setting (everyone, friends, private).
            const isFriendListHiddenByToggle = (displayUser as any).showFriendList === false;
            const fVisibility = (displayUser as any).friendListVisibility || 'everyone';

            // For self-view previews, simulate what the given viewer type sees:
            // 'friend' preview: treat viewer as a friend.
            // 'nonfriend' preview: treat viewer as a stranger.
            const effectiveViewerIsFriend = viewAsMode === 'self'
              ? true  // owner can always see their own list
              : viewAsMode === 'friend'
                ? true
                : derivedIsFriend; // real or nonfriend-preview

            const canSeeFullList = viewAsMode === 'self' || (
              !isFriendListHiddenByToggle && (
                fVisibility === 'everyone' ||
                (fVisibility === 'friends' && effectiveViewerIsFriend)
              )
            );

            // If private account and not friend, don't show the section at all (existing rule)
            if (isPrivateAccount) return null;
            // System mod account hides the Friends section entirely.
            if (isSystemModProfile) return null;

            // Re-filtering for current context visibility (already done at root level but kept for clarity)
            const currentFriendsList = friendsList;

            return (
              <div className="px-6 mb-8">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-xl text-[#ea580c] dark:text-white">Friends ({currentFriendsList.length})</h2>
                  {canSeeFullList && currentFriendsList.length > 3 && (
                    <Button
                      onClick={handleViewAllFriends}
                      variant="ghost"
                      className="px-3 py-1 rounded-full bg-[#D2B48C] dark:bg-[#2C3E50] text-[#ea580c] dark:text-white hover:bg-[#C9A978] dark:hover:bg-[#1a2533] transition-colors card-shadow text-xs"
                    >
                      View All →
                    </Button>
                  )}
                </div>

                {!canSeeFullList ? (
                   <div className="p-6 rounded-2xl bg-[#f2deb3]/30 dark:bg-white/5 border border-dashed border-[#F39C12]/20 dark:border-[#4478ea]/20 flex flex-col items-center justify-center text-center">
                     <Lock className="w-5 h-5 text-[#F39C12] dark:text-[#4478ea] mb-2 opacity-50" />
                     <p className="text-sm text-[#666666] dark:text-[#a0a0a0]">Friend list is hidden</p>
                   </div>
                ) : (
                  <div className="flex gap-3 overflow-x-auto pb-2 custom-scrollbar">
                    {friendsList.length > 0 ? (
                      friendsList.map((friend) => (
                        <motion.div
                          key={friend.id}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleFriendClick(friend)}
                          className="shrink-0 cursor-pointer flex flex-col items-center"
                        >
                          <div className="relative">
                            <Avatar className="w-16 h-16 border-2 border-[#4478ea] shadow-xl">
                              <AvatarImage src={UserService.resolveUserAvatar(friend)} />
                              <AvatarFallback>{friend.name?.[0] || '?'}</AvatarFallback>
                            </Avatar>
                            {friend.rank && ['watcher', 'legion', 'verified', 'union'].includes(friend.rank) && (
                              <div className="absolute bottom-0 right-0 rounded-full flex items-center justify-center bg-[#eddeb7] dark:bg-[#0D0D0D]" style={{ padding: 1 }}>
                                <RankBadge rank={friend.rank} size={16} />
                              </div>
                            )}
                            {/* Top-right: the rank badge already owns bottom-right. */}
                            <ActivityStatus userId={friend.id} variant="dot" style={{ top: 2, right: 2, bottom: 'auto' }} />
                          </div>
                          <p className="text-xs text-center text-[#4478ea] dark:text-white mt-1 truncate w-16">
                            {friend.name?.split(' ')[0] || 'User'}
                          </p>
                        </motion.div>
                      ))
                    ) : (
                      <div className="w-full text-center py-8 text-[#666666] dark:text-[#a0a0a0]">
                        <p className="text-sm">No friends yet</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Tribes - Hidden if private, not friend, or if user chose to hide it */}
          {(() => {
            // Tribe Privacy Rules:
            // 1. Owners (isSelf) always see their own list.
            // 2. If showTribesList is explicitly false, hide from EVERYONE else.
            // 3. Otherwise, respect the visibility setting (everyone, friends, private).
            const isTribeListHiddenByToggle = (displayUser as any).showTribesList === false;
            const tVisibility = (displayUser as any).tribeListVisibility || 'everyone';

            // Same preview simulation for Tribes
            const effectiveViewerIsFriendForTribes = viewAsMode === 'self'
              ? true
              : viewAsMode === 'friend'
                ? true
                : derivedIsFriend;

            const canSeeFullTribes = viewAsMode === 'self' || (
              !isTribeListHiddenByToggle && (
                tVisibility === 'everyone' ||
                (tVisibility === 'friends' && effectiveViewerIsFriendForTribes)
              )
            );

            // If private account and not friend, don't show the section at all (existing rule)
            if (isPrivateAccount) return null;
            // System mod account hides the Tribes section entirely.
            if (isSystemModProfile) return null;

            return (
              <div className="px-6 mb-8">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-xl text-[#ea580c] dark:text-white">Tribes ({ownedTribes.length})</h2>
                  {canSeeFullTribes && ownedTribes.length > 2 && (
                    <Button
                      variant="ghost"
                      className="px-3 py-1 rounded-full bg-[#D2B48C] dark:bg-[#2C3E50] text-[#ea580c] dark:text-white hover:bg-[#C9A978] dark:hover:bg-[#1a2533] transition-colors card-shadow text-xs"
                      onClick={() => {
                        window.dispatchEvent(new CustomEvent('navigateToAllTribes'));
                      }}
                    >
                      View All →
                    </Button>
                  )}
                </div>

                {!canSeeFullTribes ? (
                   <div className="p-6 rounded-2xl bg-white/5 border border-dashed border-[#4478ea]/20 flex flex-col items-center justify-center text-center">
                     <Lock className="w-5 h-5 text-[#4478ea] mb-2 opacity-50" />
                     <p className="text-sm text-[#666666] dark:text-[#a0a0a0]">Tribe list is hidden</p>
                   </div>
                ) : (
                  ownedTribes.length === 0 ? (
                    <div className="w-full text-center py-8 text-[#666666] dark:text-[#a0a0a0]">
                      <p className="text-sm">No tribes owned yet</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      {ownedTribes.map((tribe, index) => {
                        return (
                          <motion.div
                            key={tribe.id || index}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              window.dispatchEvent(new CustomEvent('navigateToTribeGroup', { detail: { name: tribe.name, id: tribe.id } }));
                            }}
                            className="relative h-24 rounded-xl overflow-hidden cursor-pointer card-shadow group"
                          >
                            {(tribe.bannerUrl || tribe.bannerImage || tribe.image || tribe.imageUrl || tribe.banner) ? (
                              <img 
                                src={tribe.bannerUrl || tribe.bannerImage || tribe.image || tribe.imageUrl || tribe.banner} 
                                alt={tribe.name} 
                                className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" 
                              />
                            ) : (
                              <div 
                                className="absolute inset-0 w-full h-full transition-transform duration-500 group-hover:scale-110"
                                style={{ background: getGroupGradient(tribe.id) }}
                              />
                            )}
                            <div 
                                className="absolute inset-0 backdrop-blur-[1px] transition-all duration-300 pointer-events-none" 
                                style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.1) 20%, rgba(0,0,0,0.85) 100%)' }}
                            />
                            <div className="absolute inset-x-0 bottom-0 p-2 flex flex-col items-center justify-center text-center">
                              <span 
                                className="text-white text-xs font-bold truncate w-full" 
                                style={{ textShadow: '1px 0px 0px #000, -1px 0px 0px #000, 0px 1px 0px #000, 0px -1px 0px #000, 1px 1px 0px #000, -1px -1px 0px #000, 1px -1px 0px #000, -1px 1px 0px #000, 1px 1px 3px rgba(0,0,0,0.8)' }}
                              >
                                {tribe.name}
                              </span>
                              {tribe.memberCount !== undefined && (
                                <span 
                                  className="text-white/90 text-[10px] mt-0.5 font-medium" 
                                  style={{ textShadow: '1px 0px 0px #000, -1px 0px 0px #000, 0px 1px 0px #000, 0px -1px 0px #000, 1px 1px 0px #000, -1px -1px 0px #000, 1px -1px 0px #000, -1px 1px 0px #000' }}
                                >
                                  {tribe.memberCount} members
                                </span>
                              )}
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  )
                )}
              </div>
            );
          })()}

          {/* Settings Links */}
          {viewAsMode === 'self' && (
            <div className="px-6 pb-8">
              <h2 className="text-xl text-[#4478ea] dark:text-white mb-3">Settings</h2>
              <div className="space-y-3">
                {/* First in the list, above App Settings: this is the way in to
                    the whole business feature, and it is also the permanent home
                    of the Pay CTA once an application is approved. Approval never
                    expires, so that button cannot live in a transient banner —
                    the approval notification routes here too.

                    One "Manage <name>" row per business owned, then "Create a
                    Business Profile" while slots remain. Row styling is passed
                    down rather than restated inside the component, so these stay
                    identical to the three below them. */}
                <BusinessSettingsLinks
                  ownerId={String(displayUser.id || '')}
                  rowClassName="w-full p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-[#4478ea] transition-colors flex items-center gap-3 card-shadow"
                  iconClassName="w-5 h-5 text-[#4478ea] shrink-0"
                  labelClassName="text-[#4478ea] dark:text-white"
                />
                <motion.button
                  whileHover={{ x: 5 }}
                  onClick={onSettings}
                  className="w-full p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-[#4478ea] transition-colors flex items-center gap-3 card-shadow"
                >
                  <Settings className="w-5 h-5 text-[#4478ea]" />
                  <span className="text-[#4478ea] dark:text-white">App Settings</span>
                </motion.button>
                <motion.button
                  whileHover={{ x: 5 }}
                  onClick={onSafety}
                  className="w-full p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-[#4478ea] transition-colors flex items-center gap-3 card-shadow"
                >
                  <Shield className="w-5 h-5 text-[#4478ea]" />
                  <span className="text-[#4478ea] dark:text-white">Safety & Privacy</span>
                </motion.button>
                {/* The guidelines now appear only once, right after onboarding,
                    so this is the only way back to them afterwards. */}
                <motion.button
                  whileHover={{ x: 5 }}
                  onClick={() => setShowRules(true)}
                  className="w-full p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-[#4478ea] transition-colors flex items-center gap-3 card-shadow"
                >
                  <ScrollText className="w-5 h-5 text-[#4478ea]" />
                  <span className="text-[#4478ea] dark:text-white">Rules</span>
                </motion.button>

              </div>
            </div>
          )}
            </>
          )}
        </div>
      </div>

      {/* ── Friends List Modal — rendered via portal so Framer Motion ancestor
           transforms don't create a new stacking context and break fixed positioning ── */}
      {createPortal(
        <AnimatePresence>
        {showFriendsModal && (() => {
          const allFriends = friendsList;
          const q = friendsSearch.trim().toLowerCase();
          const filtered = q.length > 0
            ? allFriends.filter((f: any) => (f.name || '').toLowerCase().includes(q) || (f.username || '').toLowerCase().includes(q))
            : allFriends;
          // Paginate: 40 per page (page 1: 1–40, page 2: 41–80, …). Only the
          // current page renders, keeping large friend lists snappy.
          const pageCount = Math.max(1, Math.ceil(filtered.length / FRIENDS_PAGE_SIZE));
          const page = Math.min(Math.max(1, friendsPage), pageCount);
          const pageItems = filtered.slice((page - 1) * FRIENDS_PAGE_SIZE, page * FRIENDS_PAGE_SIZE);
          return (
            // Centered dark panel — matches LikersModal ("Liked by") styling.
            <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
              <motion.div
                key="friends-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setShowFriendsModal(false)}
                className="absolute inset-0"
                style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
              />
              <motion.div
                key="friends-panel"
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                className="relative w-full max-w-sm shadow-2xl overflow-hidden flex flex-col rounded-3xl"
                style={{ background: '#1a1a1a', maxHeight: '80vh' }}
              >
                {/* Header */}
                <div className="p-4 flex items-center justify-between shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(68,120,234,0.15)' }}>
                      <Users style={{ width: 20, height: 20, color: '#4478ea' }} />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold" style={{ color: '#ffffff' }}>Friends</h2>
                      <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)' }}>
                        {allFriends.length} friend{allFriends.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowFriendsModal(false)}
                    className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                    style={{ background: 'rgba(255,255,255,0.1)' }}
                  >
                    <X style={{ width: 20, height: 20, color: '#ffffff' }} />
                  </button>
                </div>

                {/* Search */}
                <div className="px-4 pt-3 pb-1 shrink-0">
                  <div className="relative">
                    <Search style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: 'rgba(255,255,255,0.4)' }} />
                    <input
                      type="text"
                      value={friendsSearch}
                      onChange={(e) => { setFriendsSearch(e.target.value); setFriendsPage(1); }}
                      placeholder="Search friends…"
                      className="w-full h-10 rounded-xl text-sm outline-none"
                      style={{ paddingLeft: 36, paddingRight: 12, background: 'rgba(255,255,255,0.06)', color: '#fff', border: '1px solid rgba(255,255,255,0.08)' }}
                    />
                  </div>
                </div>

                {/* List */}
                <div className="flex-1 min-h-0 overflow-y-auto p-4 pt-2 space-y-2 custom-scrollbar" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
                  {pageItems.length > 0 ? pageItems.map((friend: any) => (
                    <div
                      key={friend.id}
                      className="flex items-center gap-3 p-2 rounded-2xl cursor-pointer transition-colors"
                      style={{ background: 'transparent' }}
                      onClick={() => { setShowFriendsModal(false); handleFriendClick(friend); }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                    >
                      <div className="relative shrink-0">
                        <Avatar className="w-10 h-10 border border-white/10">
                          <AvatarImage src={UserService.resolveUserAvatar(friend)} className="object-cover" />
                          <AvatarFallback>{friend.name?.[0] || '?'}</AvatarFallback>
                        </Avatar>
                        {friend.rank && ['watcher', 'legion', 'verified', 'union'].includes(friend.rank) && (
                          <div className="absolute bottom-0 right-0 rounded-full flex items-center justify-center" style={{ background: '#1a1a1a', padding: 1 }}>
                            <RankBadge rank={friend.rank} size={14} />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold flex items-center gap-1.5" style={{ color: '#ffffff' }}>
                          <span className="truncate">{friend.name || 'User'}</span>
                          {(friend.countryFlag || friend.country) && (
                            <FlagImage countryName={friend.countryFlag || friend.country} size="w80" className="w-4 aspect-3/2 object-contain rounded shrink-0" />
                          )}
                        </p>
                        <p className="text-xs truncate" style={{ color: 'rgba(255,255,255,0.55)' }}>@{(friend.username || '').replace(/^@/, '')}</p>
                      </div>
                      {formatFriendDate(friend.friendedAt) && (
                        <div className="shrink-0 pl-2 text-right">
                          <p className="text-sm leading-tight" style={{ color: 'rgba(255,255,255,0.35)' }}>Friends since</p>
                          <p className="text-xs font-medium leading-tight" style={{ color: 'rgba(255,255,255,0.6)' }}>{formatFriendDate(friend.friendedAt)}</p>
                        </div>
                      )}
                    </div>
                  )) : (
                    <div className="text-center py-10" style={{ color: 'rgba(255,255,255,0.55)' }}>
                      <Users style={{ width: 48, height: 48, opacity: 0.2 }} className="mx-auto mb-2" />
                      <p className="text-sm">{q.length > 0 ? 'No friends found' : 'No friends yet'}</p>
                    </div>
                  )}
                </div>

                {/* Pagination footer — only when there's more than one page */}
                {pageCount > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                    <button
                      onClick={() => setFriendsPage(p => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-semibold transition-opacity disabled:opacity-30"
                      style={{ background: 'rgba(255,255,255,0.08)', color: '#fff' }}
                    >
                      <ChevronLeft style={{ width: 16, height: 16 }} /> Prev
                    </button>
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>
                      Page {page} of {pageCount}
                    </span>
                    <button
                      onClick={() => setFriendsPage(p => Math.min(pageCount, p + 1))}
                      disabled={page >= pageCount}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-semibold transition-opacity disabled:opacity-30"
                      style={{ background: 'rgba(255,255,255,0.08)', color: '#fff' }}
                    >
                      Next <ChevronRight style={{ width: 16, height: 16 }} />
                    </button>
                  </div>
                )}
              </motion.div>
            </div>
          );
        })()}
        </AnimatePresence>,
        document.body
      )}

      {/* Same modal the once-per-account gate shows, opened on purpose here.
          onAccept only closes: acceptance is already recorded, and re-recording
          it on every read would be meaningless. */}
      <YapTabGuidelinesModal isOpen={showRules} onAccept={() => setShowRules(false)} />

      <ShareProfileModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        username={userProfile.name}
        handle={userProfile.username}
        userId={String(userProfile.id)}
        yapsCount={yapsCount}
        friendsCount={friendsCount}
        portfolio={userProfile.portfolio}
        portfolioPreviews={userProfile.portfolioPreviews}
      />
      <ProfilePictureModal
        isOpen={showPictureModal}
        onClose={() => setShowPictureModal(false)}
        imageUrl={(displayUser.portfolioPreviews?.[displayUser.profilePictureIndex ?? 0]) || displayUser.croppedAvatar || displayUser.avatar || (displayUser as any).image || ''}
        images={(() => {
          const previews = (displayUser.portfolioPreviews || []).filter((u: string) => u && u.trim().length > 0);
          if (previews.length > 0) return previews;
          const raw = (displayUser.portfolio || []).filter((u: string) => u && u.trim().length > 0);
          if (raw.length > 0) return raw;
          const avatarFallback = (displayUser.portfolioPreviews?.[displayUser.profilePictureIndex ?? 0]) || displayUser.croppedAvatar || displayUser.avatar || displayUser.image;
          return [avatarFallback].filter((u): u is string => !!u && u.trim().length > 0);
        })()}
        initialIndex={displayUser.profilePictureIndex ?? 0}
        userName={displayUser.name}
        videoUrls={displayUser.portfolioVideos}
        photoMetadata={displayUser.photoMetadata}
      />
      <ImageCropperModal
        isOpen={showCropModal}
        onClose={() => setShowCropModal(false)}
        imageUrl={cropImage || ''}
        onSave={handleSaveCroppedImage}
        aspectRatio={cropType === 'banner' ? 1200 / 300 : 1}
        title={cropType === 'banner' ? "Adjust Banner" : "Crop Profile Picture"}
        description={cropType === 'banner' ? "Fit the image into the 1200x300 frame" : "Adjust your profile picture"}
        circular={false}
      />
      {/* Portfolio Image Viewer Modal */}
      <ProfilePictureModal
        isOpen={viewingPortfolioIndex !== null}
        onClose={() => setViewingPortfolioIndex(null)}
        imageUrl={(displayUser.portfolioPreviews?.[displayUser.profilePictureIndex ?? 0]) || displayUser.croppedAvatar || displayUser.avatar || (displayUser as any).image || ''}
        images={(() => {
          const raw = displayUser.portfolio || [];
          const previews = displayUser.portfolioPreviews || [];
          return raw.map((img, i) => {
            const finalImg = (previews[i] && previews[i].trim().length > 0) ? previews[i] : (img || "");
            return isMaybeVideo(finalImg) ? "" : finalImg;
          }).filter((i): i is string => typeof i === 'string');
        })()}
        videoUrls={(() => {
          const vids = [...(displayUser.portfolioVideos || [])];
          const raw = displayUser.portfolio || [];
          for (let i = 0; i < raw.length; i++) {
            if (isMaybeVideo(raw[i]) && !vids[i]) {
              vids[i] = raw[i];
            }
          }
          return vids;
        })()}
        initialIndex={viewingPortfolioIndex !== null ? viewingPortfolioIndex : 0}
        userName={displayUser.name}
        photoMetadata={displayUser.photoMetadata}
      />

      {/* Shout-related Modals */}
      {selectedYapToShout && (
        <ShoutYapModal
          isOpen={showShoutModal}
          onClose={() => {
            setShowShoutModal(false);
            setSelectedYapToShout(null);
          }}
          yap={selectedYapToShout}
          onShoutWithCaption={handleShoutWithCaption}
          onShoutWithoutCaption={handleShoutWithoutCaption}
        />
      )}
      {selectedYapToShout && (
        <ShoutedYapOptionsModal
          isOpen={showShoutOptionsModal}
          onClose={() => {
            setShowShoutOptionsModal(false);
            setSelectedYapToShout(null);
          }}
          yap={selectedYapToShout}
          onDeleteCaption={handleDeleteCaption}
          onDeleteShout={handleDeleteShout}
          onShare={() => {
            setShowShoutOptionsModal(false);
            // Find the shouted yap to share
            const shoutedYap = selfShoutedYaps.find(shout => shout.originalYap.id === selectedYapToShout.id);
            if (shoutedYap) {
              setSelectedShoutToShare(shoutedYap);
              setShowShareShout(true);
            }
          }}
        />
      )}
      {selectedShoutToShare && (
        <ShareShoutModal
          isOpen={showShareShout}
          onClose={() => {
            setShowShareShout(false);
            setSelectedShoutToShare(null);
          }}
          shout={selectedShoutToShare}
        />
      )}
      {selectedYapToShare && (
        <ShareYapInfoModal
          isOpen={showShareYap}
          onClose={() => {
            setShowShareYap(false);
            setSelectedYapToShare(null);
          }}
          yap={selectedYapToShare}
        />
      )}
    </motion.div>
  );
}
